import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { db, functions } from '../../../firebase';
import ConsentWithdrawnDialog from '../../components/provider/ConsentWithdrawnDialog';
import useConsentWithdrawnStop from '../../hooks/useConsentWithdrawnStop';
import { doc, onSnapshot, collection, getDocs, query, where } from 'firebase/firestore';
import {
  subscribeProviderFormInstances,
  instancesById,
  profileSharedForRules,
  nonHfpInstanceIds,
} from '../../utils/providerFormInstances';
import { httpsCallable } from 'firebase/functions';
import * as Dialog from '@radix-ui/react-dialog';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  RefreshIcon,
  XIcon,
  AcademicCapIcon,
  HomeIcon,
} from '@heroicons/react/outline';
import { SearchIcon, CheckIcon, ClockIcon } from '@heroicons/react/solid';
import { formatDateTime } from '../../../utils/timestampUtils';
import { useAuth } from '../../../hooks/useAuth';
import { canTriage } from '../../../constants/userTypes';
import { apiStartTriage, apiUpdateEvidenceTags } from '../../utils/rmsApi';
import {
  PATHWAY,
  PATHWAY_LABELS,
  isCombinedConcern,
  pathwayEvidenceTags,
  pathwayReviews,
  pathwaysForConcern,
  pathwayTriage,
  resolveActingPathway,
} from '../../utils/triagePathways';
import { AUTISM_FACTOR_SOURCES, resolveOtherFactors } from '../../utils/otherFactorsSurfacing';
import {
  getDecisionAttribution,
  formatDecisionAttribution,
} from '../../utils/decisionAttribution';
import TriageDecisionDialog from '../../components/provider/TriageDecisionDialog';
import AdhdTriageDecisionForm from '../../components/provider/AdhdTriageDecisionForm';
import AutismTriageDecisionForm from '../../components/provider/AutismTriageDecisionForm';
import RmsTopNavBar from '../../components/provider/RmsTopNavBar';
import ChildIdentityHeader from '../../components/provider/ChildIdentityHeader';
import EvidenceTagControl from '../../components/provider/EvidenceTagControl';
import { tagTargetFor } from '../../utils/evidenceTagTargets';
import { getSummaryBuilder } from '../../utils/proformaSummaryBuilders';
import { notAskedReason } from '../../utils/notAskedRows';
import { isRiskStratificationVisible } from '../../constants/orgFeatures';
import useUrgentRiskAlerts from '../../hooks/useUrgentRiskAlerts';
import UrgentRiskAlertNote from '../../components/provider/UrgentRiskAlertNote';
import {
  DECISION_CHANGEABLE_NOTICE,
  FIRST_REJECTION_CHANGEABLE_NOTICE,
  initialAnswersFromReview,
  reopenedReviewFor,
  triageDecisionChange,
} from '../../utils/triageDecisionChange';
import { adhdDecisionRoster } from '../../utils/providerRoles';

/**
 * RmsTriageProforma
 * -----------------
 * Purpose:
 *   Displays all referral and triage forms in a structured proforma view for clinicians.
 *   Includes both referral forms (some with dual-respondent layout) and guardian-only
 *   triage forms.
 *
 * Features:
 *   - Side navigation for each form section with left border indicator
 *   - Two-column layout for dual-respondent forms (school + guardian)
 *   - Single-column layout for guardian-only forms (triage forms)
 *   - Referral Initiation section showing patient record data
 *   - Triage decision workflow at the end
 *
 * Form Order:
 *   1. Referral Initiation (patient record data - single respondent)
 *   2. Request for Referral (dual respondent: school + guardian)
 *   3. Reason for Referral (dual respondent)
 *   4. Education History (dual respondent)
 *   5-11. Triage Forms (guardian only)
 */

// ─────────────────────────────────────────────────────────────────────────────
// Proforma Sections Configuration
// Includes both referral forms and triage forms in the correct order
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Configuration for all proforma sections.
 * - key: The form key used for form instances (also used for schema fetch + form instance lookup)
 * - title: Display title for the section
 * - shortTitle: Abbreviated title for sidebar navigation
 * - isDualRespondent: If true, shows two-column layout (school + guardian)
 * - isReferralOverview: If true, renders the combined Referral Overview panel that
 *   merges patientRecord data with requestForReferral form data side-by-side
 * - guardianOnly: If true, only shows guardian responses (triage forms)
 */
const PROFORMA_SECTIONS = [
  // Referral Overview — combines referral initiation data (from patientRecord) with
  // the Request for Referral form responses (school + guardian). Uses key 'requestForReferral'
  // so form instance lookups and schema fetches continue to work unchanged.
  {
    key: 'requestForReferral',
    title: 'Referral Overview',
    shortTitle: 'Referral Overview',
    isReferralOverview: true,
    isDualRespondent: true,
  },
  {
    key: 'reasonForReferral',
    title: 'Reason for Referral',
    shortTitle: 'Reason for Referral',
    isDualRespondent: true,
  },
  {
    key: 'educationHistory',
    title: 'Education History',
    shortTitle: 'Education History',
    isDualRespondent: true,
  },
  // Triage forms (guardian only)
  {
    key: 'theirEarlyLife',
    title: 'Developmental History',
    shortTitle: 'Developmental History',
    guardianOnly: true,
  },
  {
    key: 'theirFamilyAndHomeLife',
    title: 'Family and Home Life',
    shortTitle: 'Family and Home Life',
    guardianOnly: true,
  },
  {
    key: 'theirLifestyle',
    title: 'Lifestyle History',
    shortTitle: 'Lifestyle History',
    guardianOnly: true,
  },
  {
    key: 'theirHistoryWithTheLaw',
    title: 'Forensic History',
    shortTitle: 'Forensic History',
    guardianOnly: true,
  },
  {
    key: 'theirFamilyHistory',
    title: 'Family History',
    shortTitle: 'Family History',
    guardianOnly: true,
  },
  {
    key: 'theirMedicalHistory',
    title: 'Medical History',
    shortTitle: 'Medical History',
    guardianOnly: true,
  },
  {
    key: 'theirMentalHealthHistory',
    title: 'Psychiatric History',
    shortTitle: 'Psychiatric History',
    guardianOnly: true,
  },
];

// Brand primary color
const BRAND_PRIMARY = '#43213e';

// ─────────────────────────────────────────────────────────────────────────────
// visibleWhen Evaluator
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Resolves a dot-separated path (e.g. "currentUser.role") to its value inside
 * a nested object.  Returns undefined when any segment is missing.
 */
function getNestedValue(obj, path) {
  return path.split('.').reduce((curr, key) => curr?.[key], obj);
}

/**
 * Recursively evaluates a `visibleWhen` predicate from a form schema against a
 * given form context object.
 *
 * Supported condition types (matching the patterns used in the referral form schemas):
 *  - { all: [...] }                   → every sub-condition must be true (AND)
 *  - { any: [...] }                   → at least one sub-condition is true (OR)
 *  - { contextEquals: { path, value } } → context value at path === value
 *  - { contextIn:     { path, values } } → context value at path is in the values array
 *
 * If the condition is null/undefined or an unrecognised shape, it defaults to
 * "visible" (true) so that sections without gating still render.
 */
function evaluateVisibleWhen(condition, context) {
  if (!condition) return true;

  // AND: all sub-conditions must pass
  if (condition.all && Array.isArray(condition.all)) {
    return condition.all.every((c) => evaluateVisibleWhen(c, context));
  }

  // OR: at least one sub-condition must pass
  if (condition.any && Array.isArray(condition.any)) {
    return condition.any.some((c) => evaluateVisibleWhen(c, context));
  }

  // Exact equality against a form-context value
  if (condition.contextEquals) {
    const { path, value } = condition.contextEquals;
    return getNestedValue(context, path) === value;
  }

  // Membership test against a set of allowed context values
  if (condition.contextIn) {
    const { path, values } = condition.contextIn;
    const actual = getNestedValue(context, path);
    return Array.isArray(values) && values.includes(actual);
  }

  // Unknown shape → default to visible
  return true;
}

/**
 * Determines whether a schema section would have been visible to a specific
 * respondent role during form completion.
 *
 * The dual-respondent form schemas (requestForReferral, reasonForReferral,
 * educationHistory) gate sections using `visibleWhen` conditions that check
 * `currentUser.role` (school_user | guardian) and `initiatedBy` (school | guardian).
 * This function simulates that evaluation so the proforma can distinguish between
 * "question not asked of this respondent" (→ N/A) and "question asked but not
 * answered" (→ No answer given).
 *
 * @param {object}  section     - A schema section object (with optional `visibleWhen`)
 * @param {string}  role        - The respondent role to check: 'school_user' | 'guardian'
 * @param {string}  initiatedBy - Who initiated the referral: 'school' | 'guardian'
 * @returns {boolean} Whether the section would have been visible to this role
 */
function isSectionVisibleForRole(section, role, initiatedBy) {
  const visibleWhen = section?.visibleWhen;
  if (!visibleWhen) return true; // No visibility conditions → visible to everyone

  // Build a simulated form context that mirrors what the respondent would have
  // seen when filling out the form
  const context = {
    pathway: 'cyp',
    initiatedBy: initiatedBy || 'school',
    currentUser: { role },
  };

  return evaluateVisibleWhen(visibleWhen, context);
}

/**
 * Pre-processes a dual-respondent form schema into merged panels for the
 * proforma layout. Collects ALL questions per CoI per respondent so that
 * multi-question CoIs can be combined into a single summary via custom builders.
 *
 * Each row carries arrays: { schoolQuestions: [], guardianQuestions: [] }.
 *
 * @param {Object} schema       - The full form schema (with a `sections` array)
 * @param {string} initiatedBy  - Who initiated the referral ('school' | 'guardian')
 * @returns {Array<{title, sectionId, rows}>}  Merged panels ready for rendering
 */
function buildMergedSections(schema, initiatedBy) {
  if (!schema?.sections) return [];

  const schoolSections = [];
  const guardianSections = [];
  const sharedSections = [];

  for (const section of schema.sections) {
    const schoolVis = isSectionVisibleForRole(section, 'school_user', initiatedBy);
    const guardianVis = isSectionVisibleForRole(section, 'guardian', initiatedBy);

    if (schoolVis && guardianVis) {
      sharedSections.push(section);
    } else if (schoolVis) {
      schoolSections.push(section);
    } else if (guardianVis) {
      guardianSections.push(section);
    }
  }

  // Index ALL guardian questions by CoI → question[] (not just the first match)
  const guardianByCoI = new Map();
  for (const section of guardianSections) {
    for (const q of section.questions || []) {
      const coi = q.categoryOfInformation;
      if (coi) {
        if (!guardianByCoI.has(coi)) guardianByCoI.set(coi, []);
        guardianByCoI.get(coi).push(q);
      }
    }
  }

  const consumedGuardianCoIs = new Set();
  const mergedPanels = [];

  // Walk school sections — group school questions by CoI within each section,
  // then merge with all guardian questions sharing the same CoI
  for (const section of schoolSections) {
    const coiOrder = [];
    const schoolCoIGroups = new Map();

    for (const q of section.questions || []) {
      const coi = q.categoryOfInformation || q.label || 'Unknown';
      if (!schoolCoIGroups.has(coi)) {
        schoolCoIGroups.set(coi, []);
        coiOrder.push(coi);
      }
      schoolCoIGroups.get(coi).push(q);
    }

    const rows = coiOrder.map((coi) => {
      const schoolQs = schoolCoIGroups.get(coi);
      const guardianQs = guardianByCoI.get(coi) || [];
      if (guardianQs.length > 0) consumedGuardianCoIs.add(coi);

      return {
        categoryOfInformation: coi,
        schoolQuestions: schoolQs,
        guardianQuestions: guardianQs,
        schoolVisible: true,
        guardianVisible: guardianQs.length > 0,
      };
    });

    mergedPanels.push({ title: section.title, sectionId: section.sectionId, rows });
  }

  // Collect guardian-only CoIs not already consumed by school-section merging.
  // Same title-coalescing logic as buildMergedSections — when a guardian section
  // title matches an existing merged panel, rows fold into that panel.
  const guardianOnlyMap = new Map();
  for (const section of guardianSections) {
    const coiOrder = [];
    const coiGroups = new Map();

    for (const q of section.questions || []) {
      const coi = q.categoryOfInformation || q.label || 'Unknown';
      if (consumedGuardianCoIs.has(coi)) continue;
      if (!coiGroups.has(coi)) {
        coiGroups.set(coi, []);
        coiOrder.push(coi);
      }
      coiGroups.get(coi).push(q);
    }

    if (coiOrder.length > 0) {
      const rows = coiOrder.map((coi) => ({
        categoryOfInformation: coi,
        schoolQuestions: [],
        guardianQuestions: coiGroups.get(coi),
        schoolVisible: false,
        guardianVisible: true,
      }));

      const existingPanel = mergedPanels.find((p) => p.title === section.title);
      if (existingPanel) {
        existingPanel.rows.push(...rows);
      } else {
        if (!guardianOnlyMap.has(section.sectionId)) {
          guardianOnlyMap.set(section.sectionId, {
            title: section.title,
            sectionId: section.sectionId,
            rows: [],
          });
        }
        guardianOnlyMap.get(section.sectionId).rows.push(...rows);
      }
    }
  }

  for (const panel of guardianOnlyMap.values()) {
    mergedPanels.push(panel);
  }

  // Shared sections (visible to both respondents, same question objects)
  for (const section of sharedSections) {
    const coiOrder = [];
    const coiGroups = new Map();

    for (const q of section.questions || []) {
      const coi = q.categoryOfInformation || q.label || 'Unknown';
      if (!coiGroups.has(coi)) {
        coiGroups.set(coi, []);
        coiOrder.push(coi);
      }
      coiGroups.get(coi).push(q);
    }

    const rows = coiOrder.map((coi) => {
      const qs = coiGroups.get(coi);
      return {
        categoryOfInformation: coi,
        schoolQuestions: qs,
        guardianQuestions: qs,
        schoolVisible: true,
        guardianVisible: true,
      };
    });

    mergedPanels.push({ title: section.title, sectionId: section.sectionId, rows });
  }

  return mergedPanels;
}

/**
 * Formats an ISO date string (YYYY-MM-DD) into a human-readable form like
 * "29th August 2019". Returns the original string unchanged if it can't be parsed.
 */
function formatISODate(isoStr) {
  if (!isoStr || typeof isoStr !== 'string') return isoStr || '';
  const match = isoStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return isoStr;

  const year = match[1];
  const month = parseInt(match[2], 10);
  const day = parseInt(match[3], 10);

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];

  const ordinal = (n) => {
    if (n >= 11 && n <= 13) return `${n}th`;
    const last = n % 10;
    if (last === 1) return `${n}st`;
    if (last === 2) return `${n}nd`;
    if (last === 3) return `${n}rd`;
    return `${n}th`;
  };

  return `${ordinal(day)} ${months[month - 1] || ''} ${year}`.trim();
}

/**
 * Formats an ISO date string as a human-readable DOB with the CYP's current
 * age in whole years appended in brackets, e.g. "26th January 2020 (6 years old)".
 */
function formatDOB(isoStr) {
  const formatted = formatISODate(isoStr);
  if (!formatted || !isoStr) return formatted;

  const match = isoStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return formatted;

  const birthDate = new Date(parseInt(match[1], 10), parseInt(match[2], 10) - 1, parseInt(match[3], 10));
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  if (age < 0) return formatted;
  return `${formatted} (${age} years old)`;
}

/**
 * Extracts data from the patient record into a CoI-keyed map of display strings.
 *
 * During referral initiation the school provides CYP details (name, DOB, address)
 * and their own contact details (name, email, phone, relationship). These live on
 * the patientRecord rather than in a form instance. This helper converts them into
 * a flat map keyed by the same categoryOfInformation values used by the guardian's
 * requestForReferral form, so they can sit side-by-side in the Referral Overview
 * panel's school column.
 *
 * @param {Object} patientRecord - The patient record document
 * @param {Object} schoolUserProfile - The school user profile (fallback for referrer details)
 * @returns {Map<string, string>} CoI → display value
 */
function buildPatientRecordSchoolRows(patientRecord, schoolUserProfile) {
  const rows = new Map();
  if (!patientRecord) return rows;

  const subject = patientRecord.subject || {};
  const referrer = patientRecord.referrer || {};

  // ── CYP details ──
  const cypName = [
    subject.firstName || patientRecord.firstName,
    subject.middleName || patientRecord.middleName,
    subject.lastName || patientRecord.lastName,
  ].filter(Boolean).join(' ');
  if (cypName) rows.set('CYP name', cypName);

  const cypDob = subject.dob || patientRecord.dateOfBirth || '';
  if (cypDob) rows.set('CYP Date of birth', formatDOB(cypDob));

  const address = subject.address || {};
  const formattedAddress = [
    address.street || address.streetAddress || '',
    address.city || address.cityTown || '',
    address.postcode || '',
  ].filter(Boolean).join(', ');
  if (formattedAddress) rows.set("Child or Young Person's Home address", formattedAddress);

  // ── Referrer details ──
  // The referrer is the school user who initiated the referral. Try the referrer
  // object first, fall back to the school user profile for older records.
  const referrerName =
    referrer.fullName ||
    [referrer.firstName, referrer.lastName].filter(Boolean).join(' ') ||
    (schoolUserProfile
      ? [schoolUserProfile.firstName, schoolUserProfile.lastName].filter(Boolean).join(' ') ||
        schoolUserProfile.displayName
      : '') ||
    '';
  if (referrerName) rows.set('Your name', referrerName);

  const referrerEmail = referrer.email || schoolUserProfile?.email || '';
  if (referrerEmail) rows.set('Your email', referrerEmail);

  const referrerPhone = referrer.phone || schoolUserProfile?.phone || '';
  if (referrerPhone) rows.set('Your phone', referrerPhone);

  const referrerRelationship = referrer.relationshipLabel || '';
  if (referrerRelationship) rows.set('Relationship to CYP', referrerRelationship);

  return rows;
}

/**
 * Builds virtual questionsGroup arrays from patient record data for the
 * LineItemDialog. Each CoI that originates from referral initiation gets a
 * breakdown of its individual component fields so the magnifying glass popup
 * can show structured detail (e.g., First name / Last name separately).
 *
 * Returns a Map<string, Array<{ question, answer }>> keyed by CoI.
 */
function buildPatientRecordDialogItems(patientRecord, schoolUserProfile) {
  const items = new Map();
  if (!patientRecord) return items;

  const subject = patientRecord.subject || {};
  const referrer = patientRecord.referrer || {};

  const vq = (label) => ({ label, type: 'text', questionId: `_pr_${label}` });

  // CYP name breakdown
  const cypFirst = subject.firstName || patientRecord.firstName || '';
  const cypMiddle = subject.middleName || patientRecord.middleName || '';
  const cypLast = subject.lastName || patientRecord.lastName || '';
  if (cypFirst || cypMiddle || cypLast) {
    const group = [];
    if (cypFirst) group.push({ question: vq('First name'), answer: cypFirst });
    if (cypMiddle) group.push({ question: vq('Middle name'), answer: cypMiddle });
    if (cypLast) group.push({ question: vq('Last name'), answer: cypLast });
    items.set('CYP name', group);
  }

  // CYP DOB
  const cypDob = subject.dob || patientRecord.dateOfBirth || '';
  if (cypDob) {
    items.set('CYP Date of birth', [{ question: vq('Date of birth'), answer: formatDOB(cypDob) }]);
  }

  // CYP Home address breakdown
  const address = subject.address || {};
  const addrStreet = address.street || address.streetAddress || '';
  const addrCity = address.city || address.cityTown || '';
  const addrPostcode = address.postcode || '';
  if (addrStreet || addrCity || addrPostcode) {
    const group = [];
    if (addrStreet) group.push({ question: vq('Street address'), answer: addrStreet });
    if (addrCity) group.push({ question: vq('City / Town'), answer: addrCity });
    if (addrPostcode) group.push({ question: vq('Postcode'), answer: addrPostcode });
    items.set("Child or Young Person's Home address", group);
  }

  // Referrer name breakdown
  const refFirst = referrer.firstName || schoolUserProfile?.firstName || '';
  const refLast = referrer.lastName || schoolUserProfile?.lastName || '';
  if (refFirst || refLast) {
    const group = [];
    if (refFirst) group.push({ question: vq('First name'), answer: refFirst });
    if (refLast) group.push({ question: vq('Last name'), answer: refLast });
    items.set('Your name', group);
  } else if (referrer.fullName || schoolUserProfile?.displayName) {
    items.set('Your name', [{ question: vq('Full name'), answer: referrer.fullName || schoolUserProfile?.displayName }]);
  }

  // Referrer email
  const refEmail = referrer.email || schoolUserProfile?.email || '';
  if (refEmail) {
    items.set('Your email', [{ question: vq('Email address'), answer: refEmail }]);
  }

  // Referrer phone — fall back to school user profile (phone is stored there
  // during the full unified referral initiation and carried forward).
  const refPhone = referrer.phone || schoolUserProfile?.phone || '';
  if (refPhone) {
    items.set('Your phone', [{ question: vq('Phone number'), answer: refPhone }]);
  }

  // Referrer relationship to CYP
  const refRelationship = referrer.relationshipLabel || '';
  if (refRelationship) {
    items.set('Relationship to CYP', [{ question: vq('Relationship to CYP'), answer: refRelationship }]);
  }

  return items;
}

// ─────────────────────────────────────────────────────────────────────────────
// Line Item Detail Dialog — Question Card
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Renders a single question's raw data inside the LineItemDialog: the question
 * text, its options (for radio/checkbox), and the respondent's selected answer.
 * Extracted as a standalone component so the dialog can render one card per
 * question when displaying a multi-question CoI group.
 */
function QuestionCard({ question, answer, allAnswers }) {
  const options = question?.options || [];
  const qtype = question?.type || 'text';

  const formatAnswerValue = () => {
    // Group questions (addresses, names) — children are stored flat in the
    // answers dict, so we look them up from allAnswers. Handled before the
    // null check because `answer` for the group key itself is typically undefined.
    if (qtype === 'group' && question?.children) {
      const children = question.children;
      const flatAnswers = allAnswers || {};
      const parts = children
        .map((child) => {
          const val = flatAnswers[child.questionId] ?? (typeof answer === 'object' ? answer?.[child.questionId] : undefined);
          const text = typeof val === 'string' ? val.trim() : '';
          return text ? `${child.label}: ${text}` : null;
        })
        .filter(Boolean);
      if (parts.length === 0) return { display: 'No answer given', hasAnswer: false };
      return { display: parts.map((p) => `\u2022 ${p}`).join('\n'), hasAnswer: true };
    }

    if (answer === null || answer === undefined || answer === '') {
      return { display: 'No answer given', hasAnswer: false };
    }

    // Radio/select - map optionId to label
    if (qtype === 'radio' || qtype === 'select') {
      if (typeof answer === 'string') {
        const opt = options.find((o) => o.optionId === answer);
        return { display: opt?.label || answer, hasAnswer: true, selectedId: answer };
      }
      if (typeof answer === 'object' && answer.optionId) {
        const opt = options.find((o) => o.optionId === answer.optionId);
        let display = opt?.label || answer.optionId;
        if (answer.otherText) display += `: ${answer.otherText}`;
        return { display, hasAnswer: true, selectedId: answer.optionId };
      }
    }

    // Checkboxes - array of optionIds
    if (qtype === 'checkboxes' && Array.isArray(answer)) {
      const selectedIds = [];
      const labels = answer.map((a) => {
        const id = typeof a === 'string' ? a : a.optionId;
        selectedIds.push(id);
        const opt = options.find((o) => o.optionId === id);
        let label = opt?.label || id;
        if (typeof a === 'object' && a.otherText) label += `: ${a.otherText}`;
        return label;
      });
      return {
        display: labels.map((l) => `• ${l}`).join('\n'),
        hasAnswer: labels.length > 0,
        selectedIds,
      };
    }

    // Milestone age group - display developmental milestone ages
    // Data shape: { milestoneId: { age: number|null, unit: 'months'|'years', notSure: boolean, notYetDoing: boolean } }
    if (qtype === 'milestoneAgeGroup' && typeof answer === 'object') {
      const milestones = question?.milestones || [];
      const lines = milestones.map((m) => {
        const val = answer[m.milestoneId];
        if (!val) return `\u2022 ${m.label}: Not specified`;
        if (val.notYetDoing) return `\u2022 ${m.label}: Not yet doing`;
        if (val.notSure) return `\u2022 ${m.label}: Not sure`;
        if (val.age == null) return `\u2022 ${m.label}: Not specified`;
        return `\u2022 ${m.label}: ${val.age} ${val.unit || 'months'}`;
      });
      return { display: lines.join('\n'), hasAnswer: true };
    }

    // Physical health measurement groups — display values with units and dates
    if (qtype === 'physicalHealthInfoGroup' && typeof answer === 'object') {
      const mt = question?.measurementType;
      const fmtDate = (obj) => {
        if (!obj) return '';
        if (obj.dateNotSure) return ' (date: not sure)';
        if (obj.date?.month && obj.date?.year) {
          const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
          return ` (${months[obj.date.month - 1]} ${obj.date.year})`;
        }
        return '';
      };
      const lines = [];
      if (mt === 'height_weight') {
        const w = answer.weight || {};
        const h = answer.height || {};
        if (w.value != null) lines.push(`\u2022 Weight: ${w.value} ${w.unit || 'kg'}${fmtDate(w)}`);
        if (h.unit === 'ft_in' && (h.ft != null || h.in != null)) {
          lines.push(`\u2022 Height: ${h.ft || 0}ft ${h.in || 0}in${fmtDate(h)}`);
        } else if (h.value != null) {
          lines.push(`\u2022 Height: ${h.value} ${h.unit || 'cm'}${fmtDate(h)}`);
        }
      } else if (mt === 'head_circumference') {
        if (answer.value != null) lines.push(`\u2022 Head circumference: ${answer.value} ${answer.unit || 'cm'}${fmtDate(answer)}`);
      } else if (mt === 'pulse_rate') {
        if (answer.unable) lines.push('\u2022 Unable to take measurement');
        else if (answer.value != null) lines.push(`\u2022 Pulse: ${answer.value} bpm${fmtDate(answer)}`);
      } else if (mt === 'blood_pressure') {
        if (answer.unable) lines.push('\u2022 Unable to take measurement');
        else if (answer.systolic != null || answer.diastolic != null) {
          lines.push(`\u2022 BP: ${answer.systolic ?? '-'}/${answer.diastolic ?? '-'} mmHg${fmtDate(answer)}`);
        }
      }
      return { display: lines.join('\n') || 'No data provided', hasAnswer: lines.length > 0 };
    }

    // Date fields — format ISO strings into human-readable form
    if (qtype === 'date' && typeof answer === 'string') {
      return { display: formatISODate(answer), hasAnswer: true };
    }

    // Default
    if (typeof answer === 'object') {
      return { display: JSON.stringify(answer, null, 2), hasAnswer: true };
    }
    return { display: String(answer), hasAnswer: true };
  };

  const { display, hasAnswer, selectedId, selectedIds } = formatAnswerValue();

  return (
    <div className="border rounded-md p-4 bg-primary/5 border-primary/20 shadow-sm">
      <div className="text-sm font-medium text-gray-900 mb-2">
        {question?.label || 'Unknown question'}
      </div>
      {question?.description && (
        <p className="text-xs text-gray-500 mb-2 italic">{question.description}</p>
      )}

      {/* Options display for radio/checkbox — selected options are highlighted,
          and any free-text follow-up (otherText) is shown inline. */}
      {options.length > 0 && (qtype === 'radio' || qtype === 'checkboxes') ? (
        <div className="grid grid-cols-2 gap-2">
          {options.map((opt) => {
            const isSelected =
              selectedId === opt.optionId ||
              (selectedIds && selectedIds.includes(opt.optionId));

            // Look up free text for this option from the raw answer
            let freeText = null;
            if (isSelected) {
              if (qtype === 'radio' && typeof answer === 'object' && answer?.otherText && answer?.optionId === opt.optionId) {
                freeText = answer.otherText;
              } else if (qtype === 'checkboxes' && Array.isArray(answer)) {
                const entry = answer.find((a) => (typeof a === 'object' ? a.optionId : a) === opt.optionId);
                if (entry && typeof entry === 'object' && entry.otherText) {
                  freeText = entry.otherText;
                }
              }
            }

            return (
              <div
                key={opt.optionId}
                className={`text-xs p-1.5 rounded ${
                  isSelected
                    ? 'bg-primary/10 border border-primary/30 text-primary font-medium'
                    : 'bg-gray-50 border border-gray-200 text-gray-600'
                }`}
              >
                {opt.label}
                {freeText && (
                  <span className="block mt-1 font-normal italic text-gray-700">
                    &ldquo;{freeText}&rdquo;
                  </span>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-sm bg-white border border-gray-200 rounded p-2 whitespace-pre-wrap">
          {hasAnswer ? display : 'No answer given'}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Line Item Detail Dialog
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Displays the raw question, answer options, and selected answer for one or
 * more line items.
 *
 * Two modes:
 *   1. Single-question mode — `question` and `answer` are provided directly.
 *      Used by the original per-question panels and single-question CoIs.
 *   2. Multi-question mode — `questionsGroup` is an array of { question, answer }
 *      pairs representing all questions in a CoI group. Used by the condensed
 *      panel so the clinician can see the full raw data behind a summary.
 *
 * When `questionsGroup` is present it takes precedence over `question`/`answer`.
 */
function LineItemDialog({ isOpen, onClose, question, answer, formName, formInstance, respondentLabel, questionsGroup, allAnswers }) {
  // Build the list of question/answer pairs to render.
  // questionsGroup (from condensed view) takes priority; otherwise wrap the
  // single question/answer into a one-element array for uniform rendering.
  const items =
    questionsGroup && questionsGroup.length > 0
      ? questionsGroup
      : question
        ? [{ question, answer }]
        : [];

  // Get submission timestamp
  const submittedAt = formInstance?.submittedAt;
  const formattedTime = formatDateTime(submittedAt, 'Unknown');

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/50 backdrop-blur-sm z-50" />
        <Dialog.Content className="radix-dialog-content-centered-motion fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-lg w-[700px] max-h-[65vh] overflow-y-auto z-50 p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-4">
            <div>
              <Dialog.Title className="text-xl font-bold text-gray-900">
                {respondentLabel || 'Responses'}
              </Dialog.Title>
              {/* Form name and submission time */}
              <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                <span>{formName || 'Unknown Form'}</span>
                {formattedTime && formattedTime !== 'Unknown' && (
                  <span className="inline-flex items-center gap-1">
                    <ClockIcon className="w-3 h-3" />
                    Submitted {formattedTime}
                  </span>
                )}
              </div>
            </div>
            <Dialog.Close asChild>
              <button
                className="p-1 rounded-full hover:bg-gray-100"
                aria-label="Close"
              >
                <XIcon className="h-6 w-6 text-gray-500" />
              </button>
            </Dialog.Close>
          </div>

          {/* Content — one QuestionCard per question in the group */}
          <div className="space-y-4">
            {items.map((item, idx) => (
              <QuestionCard
                key={item.question?.questionId || idx}
                question={item.question}
                answer={item.answer}
                allAnswers={allAnswers}
              />
            ))}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Side Navigation
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Side navigation panel showing all proforma stages.
 * Includes a "Complete Review" button at the bottom for clinicians.
 *
 * Green background + tick mark indicates the user has visited (navigated to)
 * that section at least once during this session.
 *
 * @param {Array} sections - List of proforma sections
 * @param {number} currentIndex - Currently active section index
 * @param {Function} onSelect - Callback when a section is clicked
 * @param {Set} visitedSections - Set of section indices the user has visited
 * @param {boolean} isClinicianRole - Whether the current user is a clinician
 * @param {Function} onCompleteReview - Callback to open the triage decision dialog
 * @param {boolean} isSecondReview - Whether this is the second (different-clinician) review
 * @param {boolean} allSectionsVisited - Whether all proforma sections have been visited
 */
export function ProformaSideNav({
  sections,
  currentIndex,
  onSelect,
  visitedSections,
  isClinicianRole,
  onCompleteReview,
  isSecondReview,
  allSectionsVisited,
  viewOnly = false,
  isPreview = false,
  onStartTriage,
  viewerIsFirstReviewer = false,
}) {
  return (
    <div className="w-[280px] flex-shrink-0 border-r border-gray-200 bg-gradient-to-b from-primary/5 to-primary/10 flex flex-col">
      {/* Header */}
      <div className="px-5 pt-5 pb-3 flex-shrink-0">
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
          Stages
        </span>
      </div>

      {/* Navigation items — scrollable when list exceeds available height */}
      <div className="flex-1 overflow-y-auto px-3 pb-3 space-y-1">
        {sections.map((section, idx) => {
          const isActive = idx === currentIndex;
          const isVisited = visitedSections.has(idx);

          return (
            <button
              key={section.key}
              type="button"
              className={`w-full text-left px-3 py-2.5 rounded-lg text-sm flex items-center justify-between transition-all duration-200 ease-in-out ${
                isActive
                  ? 'bg-white shadow-sm border border-gray-200 text-primary font-medium'
                  : 'text-gray-600 hover:bg-white/70 hover:text-gray-800 border border-transparent'
              }`}
              onClick={() => onSelect(idx)}
            >
              <span className="leading-tight">{section.shortTitle}</span>
              {isVisited && (
                <CheckIcon className="w-4 h-4 text-green-500 flex-shrink-0" aria-hidden="true" />
              )}
            </button>
          );
        })}
      </div>

      {/* Triage Decision Making button — pinned to the bottom of the sidebar.
          Primary CTA (filled) when all sections visited, secondary (outlined) otherwise. */}
      {isClinicianRole && (
        <div className="flex-shrink-0 px-4 py-4 border-t border-gray-200 bg-white/80">
          {isPreview && viewerIsFirstReviewer ? (
            /* TDI-400: the first reviewer cannot second-review their own case.
               The server already refuses it, so offering Start triage only led
               them to an error. Say why instead, and leave the forms readable. */
            <p className="text-center text-sm font-medium text-gray-500">
              You recorded the first review, so the second review is for a
              different clinician. You can still read the forms.
            </p>
          ) : isPreview ? (
            <button
              onClick={onStartTriage}
              className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg font-bold text-white hover:opacity-90 transition-all shadow-sm"
              style={{ backgroundColor: BRAND_PRIMARY }}
            >
              Start triage
            </button>
          ) : viewOnly ? (
            <p className="text-center text-sm font-medium text-gray-500">
              Triage complete — view only
            </p>
          ) : (
            <button
              onClick={onCompleteReview}
              className={`w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg font-bold transition-all shadow-sm ${
                allSectionsVisited
                  ? 'text-white hover:opacity-90'
                  : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
              style={allSectionsVisited ? { backgroundColor: BRAND_PRIMARY } : undefined}
            >
              {/* LSC-041: "Complete Triage" misread as finishing an assessment;
                  renamed to the agreed "Triage Decision Making". */}
              {isSecondReview ? 'Triage Decision Making (second review)' : 'Triage Decision Making'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Display Components
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Parses lightweight inline markup tokens into React elements.
 *
 * Supported tokens:
 *   **text**    → <strong> (bold)  — used for prefaces in condensed summaries
 *   _"text"_    → <em>"text"</em> — used for free-text answers (italic with quotes)
 *
 * The _"..."_ delimiter (underscore + quote) is deliberately more restrictive
 * than a bare _..._ to avoid false positives on underscores in user-typed text.
 */
function parseInlineMarkup(text) {
  if (!text) return [text || ''];

  const result = [];
  let keyIdx = 0;
  const regex = /\*\*(.+?)\*\*|_"(.+?)"_/g;
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      result.push(text.substring(lastIndex, match.index));
    }

    if (match[1] !== undefined) {
      result.push(<strong key={`b${keyIdx++}`} className="font-semibold">{match[1]}</strong>);
    } else if (match[2] !== undefined) {
      result.push(<em key={`i${keyIdx++}`}>&ldquo;{match[2]}&rdquo;</em>);
    }

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    result.push(text.substring(lastIndex));
  }

  return result.length > 0 ? result : [text];
}

/**
 * Renders a display string with rich formatting support.
 *
 * Handles three structural elements:
 *   • Bullet lines  — lines starting with "• " render with a proper bullet character
 *                      and left-indented content
 *   • Empty lines   — produce a small vertical spacer between logical groups
 *   • Regular lines — plain text with inline bold/italic markup parsed
 */
function renderRichContent(text) {
  if (!text) return null;

  const lines = text.split('\n');
  return lines.map((line, idx) => {
    if (line.startsWith('• ')) {
      return (
        <div key={idx} className="flex items-start">
          <span className="mr-1.5 flex-shrink-0 text-gray-500">•</span>
          <span>{parseInlineMarkup(line.substring(2))}</span>
        </div>
      );
    }

    if (line === '') {
      return <div key={idx} className="h-3" />;
    }

    return <div key={idx}>{parseInlineMarkup(line)}</div>;
  });
}

/**
 * Read-only text display area with rich formatting support.
 * Renders bold prefaces, italic free-text quotes, and bullet points.
 */
function DisplayTextArea({ value, placeholder = 'No answer given', disabled = false }) {
  const displayValue = value || '';
  const isEmpty = !displayValue || displayValue === 'No answer given';

  // Three visual states:
  //  1. disabled  → greyed-out background with "N/A" text (question wasn't asked of this respondent)
  //  2. isEmpty   → white background with "No answer given" placeholder (question was asked but unanswered)
  //  3. hasValue  → white background with rich-formatted content
  const stateClass = disabled
    ? 'bg-gray-100 border-gray-200 text-gray-400 italic cursor-not-allowed'
    : isEmpty
      ? 'text-gray-400 italic border-gray-200 bg-white'
      : 'text-gray-900 border-gray-200 bg-white';

  return (
    <div className={`flex-1 min-w-0 min-h-[40px] px-3 py-2 text-sm border rounded-md ${stateClass}`}>
      {disabled ? placeholder : isEmpty ? placeholder : renderRichContent(displayValue)}
    </div>
  );
}

/**
 * Formats an answer value for display in the line item row.
 * Handles various question types and returns a readable string.
 *
 * @param {Object}  question          - The schema question object
 * @param {*}       answer            - The raw answer value from the form
 * @param {Object}  [config={}]       - Optional formatting overrides
 * @param {boolean} [config.asBullets] - When true, multi-value answers (checkboxes,
 *                                       milestones) are rendered as bullet-point lists
 *                                       (one per line) instead of semicolon-separated.
 *                                       Used by the condensed CoI view for readability.
 */
function getDisplayValue(question, answer, config = {}) {
  const options = question?.options || [];
  const qtype = question?.type || 'text';

  // Group questions store their children flat in the answers dict (not nested
  // under the group's own questionId). We handle them before the null check
  // because `answer` for the group key itself will typically be undefined.
  if (qtype === 'group' && question?.children) {
    const allAnswers = config.allAnswers || {};
    const children = question.children;
    const isAddress = children.some(
      (c) => /addr/i.test(c.questionId) || /address/i.test(c.questionId)
    );
    const parts = children
      .map((child) => {
        // Try flat lookup first (standard storage), fall back to nested lookup
        const val = allAnswers[child.questionId] ?? (typeof answer === 'object' ? answer?.[child.questionId] : undefined);
        return typeof val === 'string' ? val.trim() : '';
      })
      .filter(Boolean);
    if (parts.length === 0) return 'No answer given';
    return isAddress ? parts.join(', ') : parts.join(' ');
  }

  if (answer === null || answer === undefined || answer === '') {
    return 'No answer given';
  }

  // Radio/select — map optionId to label.
  // When the selected option has a free-text follow-up (otherText), it is
  // formatted as italic text in quotes: Label - _"free text"_
  if (qtype === 'radio' || qtype === 'select') {
    if (typeof answer === 'string') {
      const opt = options.find((o) => o.optionId === answer);
      return opt?.label || answer;
    }
    if (typeof answer === 'object' && answer.optionId) {
      const opt = options.find((o) => o.optionId === answer.optionId);
      let label = opt?.label || answer.optionId;
      if (answer.otherText) label += ` - _"${answer.otherText}"_`;
      return label;
    }
  }

  // Checkboxes — map each selected option to its human-readable label.
  // Free-text follow-ups are formatted as italic quotes.
  // When config.asBullets is set we render one bullet (•) per line for the
  // condensed CoI view; otherwise labels are semicolon-separated.
  if (qtype === 'checkboxes' && Array.isArray(answer)) {
    const labels = answer.map((a) => {
      const id = typeof a === 'string' ? a : a.optionId;
      const opt = options.find((o) => o.optionId === id);
      let label = opt?.label || id;
      if (typeof a === 'object' && a.otherText) label += ` - _"${a.otherText}"_`;
      return label;
    });
    if (config.asBullets) {
      return labels.map((l) => `• ${l}`).join('\n');
    }
    return labels.join('; ');
  }

  // Milestone age group — compact format for summary display.
  // Data shape: { milestoneId: { age: number|null, unit: 'months'|'years', notSure: boolean, notYetDoing: boolean } }
  // When config.asBullets is set we render one bullet (•) per milestone for
  // the condensed CoI view; otherwise values are semicolon-separated.
  if (qtype === 'milestoneAgeGroup' && typeof answer === 'object') {
    const milestones = question?.milestones || [];
    const parts = milestones.map((m) => {
      const val = answer[m.milestoneId];
      if (!val) return null;
      if (val.notYetDoing) return `${m.label}: Not yet doing`;
      if (val.notSure) return `${m.label}: Not sure`;
      if (val.age == null) return null;
      const unitAbbrev = val.unit === 'years' ? 'yr' : 'mo';
      return `${m.label}: ${val.age}${unitAbbrev}`;
    }).filter(Boolean);
    if (config.asBullets) {
      return parts.map((p) => `• ${p}`).join('\n') || 'No answer given';
    }
    // An answer object with no actual milestone entries (e.g. the respondent
    // touched the widget without recording anything) must read as unanswered,
    // not as a recorded answer.
    return parts.join('; ') || 'No answer given';
  }

  // Physical health measurement groups — compact summary
  if (qtype === 'physicalHealthInfoGroup' && typeof answer === 'object') {
    const mt = question?.measurementType;
    const parts = [];
    if (mt === 'height_weight') {
      const w = answer.weight || {};
      const h = answer.height || {};
      if (w.value != null) parts.push(`${w.value}${w.unit || 'kg'}`);
      if (h.unit === 'ft_in' && (h.ft != null || h.in != null)) {
        parts.push(`${h.ft || 0}ft ${h.in || 0}in`);
      } else if (h.value != null) {
        parts.push(`${h.value}${h.unit || 'cm'}`);
      }
    } else if (mt === 'head_circumference') {
      if (answer.notSure) parts.push("Don't know");
      else if (answer.value != null) parts.push(`${answer.value}${answer.unit || 'cm'}`);
    } else if (mt === 'pulse_rate') {
      if (answer.unable) parts.push('Unable to measure');
      else if (answer.value != null) parts.push(`${answer.value}bpm`);
    } else if (mt === 'blood_pressure') {
      if (answer.unable) parts.push('Unable to measure');
      else if (answer.systolic != null || answer.diastolic != null) {
        parts.push(`${answer.systolic ?? '-'}/${answer.diastolic ?? '-'}mmHg`);
      }
    }
    // A measurement object with no real value (e.g. only a unit toggle was
    // touched) must read as unanswered — never as "measurements recorded".
    return parts.join('; ') || 'No answer given';
  }

  // Date fields — format ISO strings (YYYY-MM-DD) into human-readable form.
  // DOB fields additionally show the CYP's current age in years.
  if (qtype === 'date' && typeof answer === 'string') {
    const coi = question?.categoryOfInformation || '';
    if (/date of birth/i.test(coi)) return formatDOB(answer);
    return formatISODate(answer);
  }

  // Complex object (unrecognised structure)
  if (typeof answer === 'object') {
    return 'Complex answer (click to view)';
  }

  // Plain text/number
  const str = String(answer);
  return str.length > 150 ? str.slice(0, 150) + '...' : str;
}

// ─────────────────────────────────────────────────────────────────────────────
// Single Respondent Domain Panel (Guardian Only — CoI-grouped)
// ─────────────────────────────────────────────────────────────────────────────
/**
 * The Tag button on a category heading (TDI-394, R1): one per category, under
 * the heading in the left column, covering every respondent's answer in that
 * row. Renders nothing when the caller passed no tagging context (e.g. a
 * surface that does not tag) or the row has nothing to tag.
 */
export function CategoryTagControl({ tagging, label, targets }) {
  const live = (targets || []).filter(Boolean);
  if (!tagging || !live.length) return null;
  return (
    <EvidenceTagControl
      pathway={tagging.pathway}
      label={label}
      targets={live}
      tags={live.flatMap((t) => tagging.tagsFor(t.formKey, t.role, t.questionId))}
      onAdd={tagging.onAdd}
      onRemove={tagging.onRemove}
      disabled={tagging.disabled}
      disabledReason={tagging.disabledReason}
    />
  );
}

/**
 * Resolve how one Category of Information renders as a single proforma row.
 *
 * A CoI can be one question, several with a registered summary builder, or
 * several with no builder. The last case used to show only the first question,
 * so any follow-up (e.g. the self-harm "when did this last happen?" question)
 * silently dropped out of the proforma; here every answered question in the
 * group is labelled and shown, and all of them stay available via the
 * magnifier. getDisplayValue returns "No answer given" for empties, so those
 * are skipped on the row.
 */
export function resolveCoICell(questions, answers, formKey) {
  if (!questions || questions.length === 0) {
    return { displayValue: null, questionsGroup: null, question: null, answer: null, coi: null };
  }
  const coi = questions[0].categoryOfInformation;
  const builder = getSummaryBuilder(formKey, coi);

  if (builder && questions.length > 1) {
    return {
      displayValue: builder(questions, answers),
      questionsGroup: questions.map((q) => ({ question: q, answer: answers[q.questionId] })),
      question: null,
      answer: null,
      coi,
    };
  }

  if (questions.length === 1) {
    const question = questions[0];
    const answer = answers[question.questionId];
    return {
      displayValue: getDisplayValue(question, answer, { asBullets: true, allAnswers: answers }),
      questionsGroup: null,
      question,
      answer,
      coi,
    };
  }

  const parts = [];
  questions.forEach((q) => {
    const v = getDisplayValue(q, answers[q.questionId], { asBullets: true, allAnswers: answers });
    if (v != null && String(v).trim() !== '' && v !== 'No answer given') {
      parts.push(`${q.label}\n${v}`);
    }
  });
  return {
    displayValue: parts.length ? parts.join('\n\n') : null,
    questionsGroup: questions.map((q) => ({ question: q, answer: answers[q.questionId] })),
    question: null,
    answer: null,
    coi,
  };
}

/**
 * Single-respondent (guardian-only) panel that groups questions by
 * their Category of Information (CoI) instead of showing one line item per question.
 *
 * For CoIs with a single question the display is identical to the original panel
 * (minus the italicized question text). For multi-question CoIs a custom summary
 * builder from proformaSummaryBuilders.js produces a single text-box summary that
 * combines all answers into a concise, deterministic bullet-point format.
 *
 * This reduces visual clutter on the proforma by collapsing related questions
 * (e.g. the 4 "Developmental milestones" questions) into one readable line item.
 *
 * @param {Object}   section        - The schema section with questions
 * @param {Object}   answers        - The guardian's form answers (keyed by questionId)
 * @param {Object}   respondentInfo - Info about the guardian (roleLabel, name)
 * @param {Function} onViewLineItem - Callback to view raw response details
 * @param {string}   formKey        - The form key (e.g. 'theirEarlyLife'), used to
 *                                    look up the correct summary builders
 * @param {Object}   tagging        - Evidence-tagging context, or undefined to
 *                                    render the rows without a tag affordance
 */
export function SingleRespondentDomainPanel({ section, answers, respondentInfo, onViewLineItem, formKey, tagging, formContext = null, riskAlerts }) {
  const questions = section?.questions || [];

  if (questions.length === 0) {
    return null;
  }

  // ── Group questions by categoryOfInformation ──
  // Preserves insertion order so the CoI groups appear in the same sequence
  // as the original questions in the schema.
  const coiGroups = new Map();
  for (const q of questions) {
    const coi = q.categoryOfInformation || q.label || 'Unknown';
    if (!coiGroups.has(coi)) {
      coiGroups.set(coi, []);
    }
    coiGroups.get(coi).push(q);
  }

  const roleLabel = respondentInfo?.roleLabel || 'Parent / Carer';
  const fullLabel = roleLabel;

  return (
    <div className="bg-gray-50 rounded-lg border border-gray-300 text-left p-6 shadow-sm mb-6">
      {/* Panel header — identical to the original panel */}
      <div className="flex items-center mb-2">
        <h4 className="font-bold text-lg flex-1">{section.title}</h4>
        <div className="flex items-center gap-1.5 text-xs text-primary">
          <HomeIcon className="w-4 h-4" />
          <span>{fullLabel}</span>
        </div>
      </div>

      {/* One line item per CoI (instead of one per question) */}
      <div className="border-t border-gray-200 pt-4">
        {Array.from(coiGroups.entries()).map(([coi, groupQuestions]) => {
          // Every CoI resolves the same way: a summary builder collapses a
          // multi-question group, a single question renders plainly, and a
          // multi-question group with no builder shows every answered question
          // (so a follow-up never silently drops out).
          // A row the parent never saw because of a rule about the child
          // (TDI-708: the police question for a primary-age child) says so,
          // never "No answer given".
          const notAsked = groupQuestions
            .map((q) => notAskedReason(q, section, answers, formContext))
            .reduce((same, reason) => (same === undefined ? reason : (same === reason ? same : null)), undefined);
          if (notAsked) {
            return (
              <div key={coi} className="flex items-start mb-4">
                <div className="w-80 pr-4">
                  <span className="font-semibold text-sm text-gray-800">{coi}</span>
                </div>
                <div className="flex-1 flex items-start gap-2">
                  <DisplayTextArea value="" placeholder={notAsked} disabled />
                </div>
              </div>
            );
          }
          const { displayValue, questionsGroup, question, answer } = resolveCoICell(groupQuestions, answers, formKey);

          return (
            <div key={coi} className="flex items-start mb-4">
              <div className="w-80 pr-4">
                <span className="font-semibold text-sm text-gray-800">{coi}</span>
                <CategoryTagControl
                  tagging={tagging}
                  label={coi}
                  targets={[tagTargetFor({ formKey, role: 'guardian', coi, question, answers, displayValue })]}
                />
              </div>
              <div className="flex-1 flex items-start gap-2">
                <div className="flex-1 min-w-0 flex flex-col">
                  <DisplayTextArea value={displayValue} />
                  <UrgentRiskAlertNote alerts={riskAlerts} questions={groupQuestions} role="guardian" />
                </div>
                <div className="flex flex-col items-center">
                  <button
                    type="button"
                    className="cursor-pointer mt-2 p-1 rounded hover:bg-primary/10 text-primary"
                    onClick={() => (questionsGroup
                      ? onViewLineItem(null, null, fullLabel, questionsGroup, answers, 'guardian')
                      : onViewLineItem(question, answer, fullLabel, null, answers, 'guardian'))}
                    aria-label="View raw responses"
                  >
                    <SearchIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Dual Respondent Domain Panel (School + Guardian — CoI-grouped)
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Dual-respondent panel that groups questions by Category of Information (CoI).
 *
 * Renders multi-select answers as bullet-point lists and consolidates
 * multi-question CoIs into a single summary via custom builders.
 * The magnifying glass opens the dialog with ALL questions in a multi-Q group.
 *
 * Accepts panels produced by buildMergedSections(), where each row
 * carries arrays of questions per respondent (schoolQuestions[], guardianQuestions[]).
 *
 * @param {Object}   panel              - Merged panel { title, sectionId, rows }
 * @param {string}   formKey            - The form key for builder lookup (e.g. 'educationHistory')
 * @param {Object}   schoolAnswers      - The school user's form answers (keyed by questionId)
 * @param {Object}   guardianAnswers    - The guardian's form answers (keyed by questionId)
 * @param {Object}   schoolInfo         - Info about school user (roleLabel, name)
 * @param {Object}   guardianInfo       - Info about guardian (roleLabel, name)
 * @param {boolean}  hasSchoolInstance   - Whether a school form instance exists at all
 * @param {boolean}  hasGuardianInstance - Whether a guardian form instance exists at all
 * @param {Function} onViewLineItem     - Callback to view raw response details
 */
function DualRespondentDomainPanel({
  panel,
  formKey,
  schoolAnswers,
  guardianAnswers,
  schoolInfo,
  guardianInfo,
  hasSchoolInstance = true,
  hasGuardianInstance = true,
  onViewLineItem,
  tagging,
  riskAlerts,
}) {
  const rows = panel?.rows || [];

  if (rows.length === 0) {
    return null;
  }

  const schoolFullLabel = 'School Representative';
  const guardianFullLabel = 'Parent / Carer';

  const anySchoolVisible = rows.some((r) => r.schoolVisible);
  const anyGuardianVisible = rows.some((r) => r.guardianVisible);

  /**
   * Builds the display value for one respondent's side of a CoI row.
   *
   * When the respondent has 2+ questions for this CoI and a custom builder
   * exists, it produces a consolidated summary and returns the full
   * questionsGroup for the magnifying-glass dialog. Otherwise falls back
   * to getDisplayValue() with bullet formatting for multi-select answers.
   */
  const buildRespondentCell = (questions, respAnswers) =>
    resolveCoICell(questions, respAnswers, formKey);

  return (
    <div className="bg-gray-50 rounded-lg border border-gray-300 text-left p-6 shadow-sm mb-6">
      {/* Panel header with two respondent columns */}
      <div className="flex items-center mb-2">
        <h4 className="font-bold text-lg w-64">{panel.title}</h4>
        <div className="flex-1 flex">
          <div className={`flex-1 flex items-center justify-center gap-1.5 text-xs ${anySchoolVisible ? 'text-primary' : 'text-gray-400'}`}>
            <AcademicCapIcon className="w-4 h-4" />
            <span className="truncate max-w-[200px]" title={schoolFullLabel}>{schoolFullLabel}</span>
          </div>
          <div className={`flex-1 flex items-center justify-center gap-1.5 text-xs ${anyGuardianVisible ? 'text-primary' : 'text-gray-400'}`}>
            <HomeIcon className="w-4 h-4" />
            <span className="truncate max-w-[200px]" title={guardianFullLabel}>{guardianFullLabel}</span>
          </div>
        </div>
      </div>

      {/* One row per CoI — no italicized question text in condensed mode */}
      <div className="border-t border-gray-200 pt-4">
        {rows.map((row, idx) => {
          const { categoryOfInformation, schoolQuestions, guardianQuestions, schoolVisible, guardianVisible } = row;

          const schoolActive = schoolVisible && hasSchoolInstance;
          const guardianActive = guardianVisible && hasGuardianInstance;

          const school = schoolActive
            ? buildRespondentCell(schoolQuestions, schoolAnswers)
            : { displayValue: null, questionsGroup: null, question: null, answer: null };

          const guardian = guardianActive
            ? buildRespondentCell(guardianQuestions, guardianAnswers)
            : { displayValue: null, questionsGroup: null, question: null, answer: null };

          // One target per respondent: the category's Tag button lists both
          // answers' lines, so a tag always records which of the two said the
          // thing it points at.
          const schoolTagTarget = schoolActive
            ? tagTargetFor({
              formKey,
              role: 'school_user',
              coi: categoryOfInformation,
              question: school.question,
              answers: schoolAnswers,
              displayValue: school.displayValue,
            })
            : null;
          const guardianTagTarget = guardianActive
            ? tagTargetFor({
              formKey,
              role: 'guardian',
              coi: categoryOfInformation,
              question: guardian.question,
              answers: guardianAnswers,
              displayValue: guardian.displayValue,
            })
            : null;

          return (
            <div key={`${panel.sectionId}-${idx}`} className="flex items-start mb-4">
              {/* CoI label, with the category's one Tag button (TDI-394) */}
              <div className="w-64 pr-4">
                <span className="font-semibold text-sm text-gray-800">{categoryOfInformation}</span>
                <CategoryTagControl
                  tagging={tagging}
                  label={categoryOfInformation}
                  targets={[schoolTagTarget, guardianTagTarget]}
                />
              </div>

              {/* Two-column answers — columns stretch to match the taller side */}
              <div className="flex-1 flex gap-4 items-stretch">
                {/* School column — no items-start so the text area fills the column height */}
                <div className="flex-1 flex gap-1">
                  <div className="flex-1 min-w-0 flex flex-col">
                    <DisplayTextArea
                      value={school.displayValue}
                      placeholder={schoolActive ? 'No answer given' : 'N/A'}
                      disabled={!schoolActive}
                    />
                    {schoolActive && (
                      <UrgentRiskAlertNote alerts={riskAlerts} questions={schoolQuestions} role="school_user" />
                    )}
                  </div>
                  {/* Fixed-width slot for the magnifying glass — always rendered for
                      consistent text-box widths even when the button itself is hidden */}
                  <div className="w-7 flex-shrink-0">
                    {schoolActive && schoolQuestions.length > 0 && (
                      <div className="flex flex-col items-center">
                        <button
                          type="button"
                          className="cursor-pointer mt-2 p-1 rounded hover:bg-primary/10 text-primary"
                          onClick={() => {
                            if (school.questionsGroup) {
                              onViewLineItem(null, null, schoolFullLabel, school.questionsGroup, schoolAnswers, 'school_user');
                            } else {
                              onViewLineItem(school.question, school.answer, schoolFullLabel, null, schoolAnswers, 'school_user');
                            }
                          }}
                          aria-label="View school response"
                        >
                          <SearchIcon className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Guardian column */}
                <div className="flex-1 flex gap-1">
                  <div className="flex-1 min-w-0 flex flex-col">
                    <DisplayTextArea
                      value={guardian.displayValue}
                      placeholder={guardianActive ? 'No answer given' : 'N/A'}
                      disabled={!guardianActive}
                    />
                    {guardianActive && (
                      <UrgentRiskAlertNote alerts={riskAlerts} questions={guardianQuestions} role="guardian" />
                    )}
                  </div>
                  <div className="w-7 flex-shrink-0">
                    {guardianActive && guardianQuestions.length > 0 && (
                      <div className="flex flex-col items-center">
                        <button
                          type="button"
                          className="cursor-pointer mt-2 p-1 rounded hover:bg-primary/10 text-primary"
                          onClick={() => {
                            if (guardian.questionsGroup) {
                              onViewLineItem(null, null, guardianFullLabel, guardian.questionsGroup, guardianAnswers, 'guardian');
                            } else {
                              onViewLineItem(guardian.question, guardian.answer, guardianFullLabel, null, guardianAnswers, 'guardian');
                            }
                          }}
                          aria-label="View guardian response"
                        >
                          <SearchIcon className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Referral Overview Panel (Combined Referral Initiation + Request for Referral)
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Combines referral initiation data (from patientRecord) with Request for
 * initiation — this data lives on the patientRecord, not in a form instance.
 * The guardian later confirms CYP details and provides additional information
 * via the requestForReferral form. This panel shows both side-by-side so
 * the clinician can compare school-provided and guardian-provided data.
 *
 * Panels are organised into logical groups:
 *   1. CYP Details (school-provided basics vs guardian-confirmed + expanded details)
 *   2. Respondent Details (school referrer vs guardian contact info)
 *   3. Referral Details (school-only: education setting, previous referrals, etc.)
 *   4. Parental or Legal Responsibility (guardian-only)
 *   5. Consent and Permissions (guardian-only)
 *   6. Health Professionals (guardian-only)
 *   7. Social Care & Safeguarding (both respondents)
 */
export function ReferralOverviewPanel({
  patientRecord,
  schoolUserProfile,
  schema,
  formKey,
  schoolAnswers,
  guardianAnswers,
  hasSchoolInstance,
  hasGuardianInstance,
  onViewLineItem,
  tagging,
  riskAlerts,
}) {
  // Extract patient record data into CoI-keyed display values for the school column.
  // These fill in for CoIs where the school's data came from referral initiation
  // rather than the requestForReferral form.
  const prSchoolRows = useMemo(
    () => buildPatientRecordSchoolRows(patientRecord, schoolUserProfile),
    [patientRecord, schoolUserProfile]
  );

  // Virtual questionsGroup items for the LineItemDialog when the school data
  // originates from the patient record rather than a form instance.
  const prDialogItems = useMemo(
    () => buildPatientRecordDialogItems(patientRecord, schoolUserProfile),
    [patientRecord, schoolUserProfile]
  );

  const initiatedBy = patientRecord?.referrerType || 'school';

  // Classify the requestForReferral schema sections by respondent role
  const { schoolSections, guardianSections } = useMemo(() => {
    const school = [];
    const guardian = [];
    for (const section of schema?.sections || []) {
      const schoolVis = isSectionVisibleForRole(section, 'school_user', initiatedBy);
      const guardianVis = isSectionVisibleForRole(section, 'guardian', initiatedBy);
      if (schoolVis) school.push(section);
      if (guardianVis) guardian.push(section);
    }
    return { schoolSections: school, guardianSections: guardian };
  }, [schema, initiatedBy]);

  // Index ALL guardian questions by CoI for quick lookup
  const guardianByCoI = useMemo(() => {
    const map = new Map();
    for (const section of guardianSections) {
      for (const q of section.questions || []) {
        const coi = q.categoryOfInformation;
        if (coi) {
          if (!map.has(coi)) map.set(coi, []);
          map.get(coi).push(q);
        }
      }
    }
    return map;
  }, [guardianSections]);

  // Index ALL school form questions by CoI
  const schoolByCoI = useMemo(() => {
    const map = new Map();
    for (const section of schoolSections) {
      for (const q of section.questions || []) {
        const coi = q.categoryOfInformation;
        if (coi) {
          if (!map.has(coi)) map.set(coi, []);
          map.get(coi).push(q);
        }
      }
    }
    return map;
  }, [schoolSections]);

  const schoolFullLabel = 'School Representative';
  const guardianFullLabel = 'Parent / Carer';

  // ── Row rendering helpers ──

  /**
   * Renders a single CoI row with two columns. The school side can come from
   * either the patientRecord (with virtual dialog items) or form answers
   * (with real schema questions). The guardian side always comes from form answers.
   */
  const renderRow = (coi, {
    schoolValue = null,
    schoolFromPatientRecord = false,
    schoolQuestions = [],
    guardianQuestions = [],
    schoolActive = true,
    guardianActive = true,
    displayLabel = null,
  } = {}) => {
    // Resolve school display value
    let schoolDisplay = schoolValue;
    let schoolQuestionsGroup = null;
    let schoolQuestion = null;
    let schoolAnswer = null;

    if (schoolFromPatientRecord && schoolDisplay) {
      // Patient record data — use virtual dialog items for the magnifying glass
      schoolQuestionsGroup = prDialogItems.get(coi) || null;
    } else if (!schoolFromPatientRecord && schoolQuestions.length > 0 && schoolActive) {
      const cell = resolveCoICell(schoolQuestions, schoolAnswers, formKey);
      schoolDisplay = cell.displayValue;
      schoolQuestionsGroup = cell.questionsGroup;
      schoolQuestion = cell.question;
      schoolAnswer = cell.answer;
    }

    // Resolve guardian display value
    let guardianDisplay = null;
    let guardianQuestionsGroup = null;
    let guardianQuestion = null;
    let guardianAnswer = null;

    if (guardianQuestions.length > 0 && guardianActive) {
      const cell = resolveCoICell(guardianQuestions, guardianAnswers, formKey);
      guardianDisplay = cell.displayValue;
      guardianQuestionsGroup = cell.questionsGroup;
      guardianQuestion = cell.question;
      guardianAnswer = cell.answer;
    }

    // Tagging targets. The school side is only taggable when it came from the
    // school's own form: the rest is referral-initiation data copied off the
    // patient record, which has no form answer to point a tag at.
    const schoolTagTarget = schoolActive && !schoolFromPatientRecord
      ? tagTargetFor({
        formKey,
        role: 'school_user',
        coi,
        question: schoolQuestion,
        answers: schoolAnswers,
        displayValue: schoolDisplay,
      })
      : null;
    const guardianTagTarget = guardianActive
      ? tagTargetFor({
        formKey,
        role: 'guardian',
        coi,
        question: guardianQuestion,
        answers: guardianAnswers,
        displayValue: guardianDisplay,
      })
      : null;

    return (
      <div key={coi} className="flex items-start mb-4">
        <div className="w-64 pr-4">
          <span className="font-semibold text-sm text-gray-800">{displayLabel || coi}</span>
          <CategoryTagControl
            tagging={tagging}
            label={displayLabel || coi}
            targets={[schoolTagTarget, guardianTagTarget]}
          />
        </div>
        <div className="flex-1 flex gap-4 items-stretch">
          {/* School column */}
          <div className="flex-1 flex gap-1">
            <div className="flex-1 min-w-0 flex flex-col">
              <DisplayTextArea
                value={schoolActive ? schoolDisplay : null}
                placeholder={schoolActive ? 'No answer given' : 'N/A'}
                disabled={!schoolActive}
              />
              {schoolActive && !schoolFromPatientRecord && (
                <UrgentRiskAlertNote alerts={riskAlerts} questions={schoolQuestions} role="school_user" />
              )}
            </div>
            <div className="w-7 flex-shrink-0">
              {schoolActive && (schoolQuestionsGroup || schoolQuestions.length > 0) && (
                <div className="flex flex-col items-center">
                  <button
                    type="button"
                    className="cursor-pointer mt-2 p-1 rounded hover:bg-primary/10 text-primary"
                    onClick={() => {
                      if (schoolQuestionsGroup) {
                        onViewLineItem(null, null, schoolFullLabel, schoolQuestionsGroup, schoolAnswers, schoolFromPatientRecord ? null : 'school_user');
                      } else {
                        onViewLineItem(schoolQuestion, schoolAnswer, schoolFullLabel, null, schoolAnswers, 'school_user');
                      }
                    }}
                    aria-label="View school response"
                  >
                    <SearchIcon className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
          {/* Guardian column */}
          <div className="flex-1 flex gap-1">
            <div className="flex-1 min-w-0 flex flex-col">
              <DisplayTextArea
                value={guardianActive ? guardianDisplay : null}
                placeholder={guardianActive ? 'No answer given' : 'N/A'}
                disabled={!guardianActive}
              />
              {guardianActive && (
                <UrgentRiskAlertNote alerts={riskAlerts} questions={guardianQuestions} role="guardian" />
              )}
            </div>
            <div className="w-7 flex-shrink-0">
              {guardianActive && guardianQuestions.length > 0 && (
                <div className="flex flex-col items-center">
                  <button
                    type="button"
                    className="cursor-pointer mt-2 p-1 rounded hover:bg-primary/10 text-primary"
                    onClick={() => {
                      if (guardianQuestionsGroup) {
                        onViewLineItem(null, null, guardianFullLabel, guardianQuestionsGroup, guardianAnswers, 'guardian');
                      } else {
                        onViewLineItem(guardianQuestion, guardianAnswer, guardianFullLabel, null, guardianAnswers, 'guardian');
                      }
                    }}
                    aria-label="View guardian response"
                  >
                    <SearchIcon className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  /**
   * Renders a panel card with a title, two-column header, and rows.
   */
  const renderPanel = (title, rows, { anySchoolVisible = true, anyGuardianVisible = true } = {}) => (
    <div className="bg-gray-50 rounded-lg border border-gray-300 text-left p-6 shadow-sm mb-6">
      <div className="flex items-center mb-2">
        <h4 className="font-bold text-lg w-64">{title}</h4>
        <div className="flex-1 flex">
          <div className={`flex-1 flex items-center justify-center gap-1.5 text-xs ${anySchoolVisible ? 'text-primary' : 'text-gray-400'}`}>
            <AcademicCapIcon className="w-4 h-4" />
            <span className="truncate max-w-[200px]">{schoolFullLabel}</span>
          </div>
          <div className={`flex-1 flex items-center justify-center gap-1.5 text-xs ${anyGuardianVisible ? 'text-primary' : 'text-gray-400'}`}>
            <HomeIcon className="w-4 h-4" />
            <span className="truncate max-w-[200px]">{guardianFullLabel}</span>
          </div>
        </div>
      </div>
      <div className="border-t border-gray-200 pt-4">
        {rows}
      </div>
    </div>
  );

  // ── Build panels ──

  // Helper: look up guardian questions for a CoI, respecting whether we have an instance
  const gqs = (coi) => guardianByCoI.get(coi) || [];
  const sqs = (coi) => schoolByCoI.get(coi) || [];

  // Panel 1: Respondent Details
  // School col: referrer name/email/phone/relationship from patientRecord + time-known from form
  // Guardian col: guardian's own details from form
  const respondentRows = (
    <>
      {renderRow('Your name', { displayLabel: 'Name', schoolValue: prSchoolRows.get('Your name') || null, schoolFromPatientRecord: true, guardianQuestions: gqs('Your name'), guardianActive: hasGuardianInstance })}
      {renderRow('Your email', { displayLabel: 'Email address', schoolValue: prSchoolRows.get('Your email') || null, schoolFromPatientRecord: true, guardianQuestions: gqs('Your email'), guardianActive: hasGuardianInstance })}
      {renderRow('Your phone', { displayLabel: 'Phone number', schoolValue: prSchoolRows.get('Your phone') || null, schoolFromPatientRecord: true, guardianQuestions: gqs('Your phone'), guardianActive: hasGuardianInstance })}
      {renderRow('Relationship to CYP', { schoolValue: prSchoolRows.get('Relationship to CYP') || null, schoolFromPatientRecord: true, guardianQuestions: gqs('Relationship to CYP'), guardianActive: hasGuardianInstance })}
      {renderRow('Time known CYP', { schoolQuestions: sqs('Time known CYP'), schoolActive: hasSchoolInstance, guardianQuestions: gqs('Time known CYP'), guardianActive: hasGuardianInstance })}
    </>
  );

  // Panel 2: CYP Details
  // School col: CYP name/DOB/address from patientRecord, rest N/A
  // Guardian col: all CYP detail questions from form
  const cypCoIs = [
    'CYP name', 'CYP Date of birth', "Child or Young Person's Home address",
    'Preferred name', 'Sex at birth', 'Gender identity', 'Pronouns',
    'Ethnic background', 'Religion or belief', 'NHS number',
    'Interpreter needs', 'Extra support needs',
  ];
  const cypRows = cypCoIs.map((coi) => {
    const prValue = prSchoolRows.get(coi);
    if (prValue) {
      return renderRow(coi, { schoolValue: prValue, schoolFromPatientRecord: true, guardianQuestions: gqs(coi), guardianActive: hasGuardianInstance });
    }
    return renderRow(coi, { schoolActive: false, guardianQuestions: gqs(coi), guardianActive: hasGuardianInstance });
  });

  // Panel 3: Referral Details (school-only)
  // These are from the requestForReferral school form instance, not patientRecord
  const referralDetailCoIs = [
    'Education setting name', 'Education setting address', 'Education setting phone',
    'Referrer job role', 'Previous referrals', 'CYP awareness of referral',
  ];
  const referralDetailRows = referralDetailCoIs.map((coi) =>
    renderRow(coi, { schoolQuestions: sqs(coi), schoolActive: hasSchoolInstance, guardianQuestions: [], guardianActive: false })
  );

  // Panel 4: Parental or Legal Responsibility (guardian-only)
  const legalRows = (
    <>
      {renderRow('Additional responsible persons', { schoolActive: false, guardianQuestions: gqs('Additional responsible persons'), guardianActive: hasGuardianInstance })}
    </>
  );

  // Panel 5: Consent and Permissions (guardian-only)
  const consentCoIs = [
    'CYP consent to referral', 'Guardian agreement',
    'Share with health professionals', 'Share with social services',
    'Share with education setting',
  ];
  const consentRows = consentCoIs.map((coi) =>
    renderRow(coi, { schoolActive: false, guardianQuestions: gqs(coi), guardianActive: hasGuardianInstance })
  );

  // Panel 6: Health Professionals (guardian-only)
  const healthCoIs = ['GP surgery name', 'GP surgery address', 'Other services involved'];
  const healthRows = healthCoIs.map((coi) =>
    renderRow(coi, { schoolActive: false, guardianQuestions: gqs(coi), guardianActive: hasGuardianInstance })
  );

  // Panel 7: Social Care & Safeguarding (both respondents)
  const safeguardingCoIs = [
    'Self-harm or suicidal ideation',
    'Social care involvement', 'Social care plans', 'Care Arrangements',
  ];
  const safeguardingRows = safeguardingCoIs.map((coi) => {
    const schoolQs = sqs(coi);
    const guardianQs = gqs(coi);
    return renderRow(coi, {
      schoolQuestions: schoolQs,
      schoolActive: schoolQs.length > 0 && hasSchoolInstance,
      guardianQuestions: guardianQs,
      guardianActive: hasGuardianInstance,
    });
  });

  // Catch-all: any CoI present on the form but not placed by the fixed panels
  // above (e.g. 'Parent / Carer contact details', which carries the school's
  // "told to expect email" and guardian-nomination questions) still shows in an
  // "Other referral information" panel, so no Request for Referral answer can
  // silently disappear.
  const placedCoIs = new Set([
    'Your name', 'Your email', 'Your phone', 'Relationship to CYP', 'Time known CYP',
    ...cypCoIs, ...referralDetailCoIs, 'Additional responsible persons',
    ...consentCoIs, ...healthCoIs, ...safeguardingCoIs,
  ]);
  const extraCoIs = [];
  for (const coi of schoolByCoI.keys()) {
    if (!placedCoIs.has(coi) && !extraCoIs.includes(coi)) extraCoIs.push(coi);
  }
  for (const coi of guardianByCoI.keys()) {
    if (!placedCoIs.has(coi) && !extraCoIs.includes(coi)) extraCoIs.push(coi);
  }
  const otherRows = extraCoIs.map((coi) => {
    const schoolQs = sqs(coi);
    const guardianQs = gqs(coi);
    return renderRow(coi, {
      schoolQuestions: schoolQs,
      schoolActive: schoolQs.length > 0 && hasSchoolInstance,
      guardianQuestions: guardianQs,
      guardianActive: guardianQs.length > 0 && hasGuardianInstance,
    });
  });

  return (
    <>
      {renderPanel('CYP Details', <>{cypRows}</>)}
      {renderPanel('Respondent Details', respondentRows)}
      {renderPanel('Referral Details', <>{referralDetailRows}</>, { anyGuardianVisible: false })}
      {renderPanel('Parental or Legal Responsibility', legalRows, { anySchoolVisible: false })}
      {renderPanel('Consent and Permissions', <>{consentRows}</>, { anySchoolVisible: false })}
      {renderPanel('Health Professionals', <>{healthRows}</>, { anySchoolVisible: false })}
      {renderPanel('Social Care & Safeguarding', <>{safeguardingRows}</>)}
      {extraCoIs.length > 0 && renderPanel(
        'Other referral information',
        <>{otherRows}</>,
        {
          anySchoolVisible: extraCoIs.some((coi) => sqs(coi).length > 0),
          anyGuardianVisible: extraCoIs.some((coi) => gqs(coi).length > 0),
        },
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Existing Decision Banner (commented out)
// ─────────────────────────────────────────────────────────────────────────────
// function ExistingDecisionBanner({ decision, onEdit }) {
//   if (!decision) return null;

//   const pathwayLabels = {
//     autism: 'Autism',
//     adhd: 'ADHD',
//     combined: 'Combined Autism and ADHD',
//     complex: 'Complex',
//   };

//   const pathwayLabel = pathwayLabels[decision.pathwayAllocation] || decision.pathwayAllocation;
//   const decidedAt = decision.decidedAt?.toDate
//     ? formatDateTime(decision.decidedAt.toDate(), 'Unknown')
//     : formatDateTime(decision.decidedAt, 'Unknown');

//   return (
//     <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-5 mb-6">
//       <div className="flex items-start justify-between">
//         <div className="flex-1">
//           <div className="flex items-center gap-2 mb-3">
//             <CheckIcon className="w-5 h-5 text-emerald-600" />
//             <h3 className="font-semibold text-emerald-900">Triage Decision Recorded</h3>
//             {decision.version > 1 && (
//               <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
//                 v{decision.version}
//               </span>
//             )}
//           </div>

//           <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
//             <div>
//               <span className="text-emerald-700 font-medium">Pathway: </span>
//               <span className="text-emerald-900 font-semibold">{pathwayLabel}</span>
//             </div>
//             <div>
//               <span className="text-emerald-700 font-medium">Assigned Clinician: </span>
//               <span className="text-emerald-900">{decision.assignedClinician}</span>
//             </div>
//             {decision.appointmentDetails?.dateTime && (
//               <div className="col-span-2">
//                 <span className="text-emerald-700 font-medium">Appointment: </span>
//                 <span className="text-emerald-900">
//                   {new Date(decision.appointmentDetails.dateTime).toLocaleString('en-GB', {
//                     weekday: 'short',
//                     day: 'numeric',
//                     month: 'short',
//                     year: 'numeric',
//                     hour: '2-digit',
//                     minute: '2-digit',
//                   })}
//                 </span>
//               </div>
//             )}
//             <div className="col-span-2">
//               <span className="text-emerald-700 font-medium">Next Steps: </span>
//               <span className="text-emerald-900">{decision.recommendedNextSteps}</span>
//             </div>
//           </div>

//           <div className="mt-3 text-xs text-emerald-600">
//             {decision.decidedBy?.clinicianName && (
//               <span>Decided by {decision.decidedBy.clinicianName}</span>
//             )}
//             {decidedAt && decidedAt !== 'Unknown' && (
//               <span className="ml-2">• {decidedAt}</span>
//             )}
//           </div>
//         </div>

//         {onEdit && (
//           <button
//             onClick={onEdit}
//             className="flex-shrink-0 ml-4 px-3 py-1.5 text-sm font-medium text-emerald-700 bg-emerald-100 hover:bg-emerald-200 rounded-md transition-colors"
//           >
//             Edit
//           </button>
//         )}
//       </div>
//     </div>
//   );
// }

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────
export default function RmsTriageProforma() {
  const navigate = useNavigate();
  const { patientRecordId } = useParams();
  const [searchParams] = useSearchParams();
  // Preview mode (TD-8/A8): the clinician is reviewing the school + parent forms
  // before committing. The proforma renders read-only — no decision can be made
  // until they Start triage (which assigns + locks the patient) and confirm.
  const isPreview = searchParams.get('preview') === '1';

  const { user, claims } = useAuth();
  const orgId = claims?.rmsOrgId;
  // Display-only: some orgs hide the automated risk-stratification surfacing
  // (still computed/stored server-side). See src/rms/constants/orgFeatures.js.
  const riskStratificationVisible = isRiskStratificationVisible(orgId);
  // TDI-701: urgent risk alerts sent for this referral, noted under the
  // answer that raised each one.
  const { alerts: urgentRiskAlerts } = useUrgentRiskAlerts(orgId, patientRecordId);
  // Who may triage: RMS Clinicians and Clinical Org. Admins (assignable). Gates
  // the "Complete Triage" CTA and the editable proforma (TD-40). Named
  // "isClinicianRole" for continuity with existing usages below.
  const isClinicianRole = canTriage(claims);

  // State
  const [formInstances, setFormInstances] = useState({});
  const [schemas, setSchemas] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [listenerError, setListenerError] = useState(null);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const contentAreaRef = useRef(null);
  const [patientRecord, setPatientRecord] = useState(null);

  // C8: resolve which pathway this clinician is triaging FIRST — the factor
  // surfacing map and all the triage state below (reviews, lock/status,
  // second-review context) must be read for that pathway. Single-concern
  // referrals resolve to their one pathway; combined referrals resolve from the
  // active tab (?pathway=) or, failing that, the clinician's specialism.
  const actingPathway = resolveActingPathway(
    patientRecord,
    searchParams.get('pathway'),
    claims?.specialism,
  );

  // "Other Factors" auto-surfacing (TD-185): resolve each factor's supporting
  // answers from the submitted forms so the decision step shows real evidence
  // instead of "Not captured". Read-only; the clinician still notes on each
  // factor. The factor map is pathway-specific (the autism wizard's regrouped
  // factors have their own keys). Secondary referrals have no HFP, so
  // HFP-sourced items fall back to their other sources / Not captured (handled
  // in the resolver).
  const surfacedFactors = useMemo(
    () => resolveOtherFactors({
      formInstances,
      schemas,
      schoolPhase: patientRecord?.schoolPhase || patientRecord?.school?.schoolType || null,
      factorSources: actingPathway === PATHWAY.ASD ? AUTISM_FACTOR_SOURCES : undefined,
    }),
    [formInstances, schemas, patientRecord, actingPathway],
  );

  // Track which sections the user has visited (navigated to at least once)
  // Initialized with the first section (index 0) as visited
  const [visitedSections, setVisitedSections] = useState(() => new Set([0]));

  // Dialog state — supports both single-question mode (question/answer) and
  // multi-question mode (questionsGroup) for the CoI-grouped view
  const [lineItemDialog, setLineItemDialog] = useState({
    isOpen: false,
    question: null,
    answer: null,
    questionsGroup: null,
    allAnswers: null,
    formName: '',
    formInstance: null,
    respondentLabel: '',
  });
  const [showDecisionDialog, setShowDecisionDialog] = useState(false);
  // The clinician's unsent decision answers, per referral and pathway, kept
  // here while they go back to the history review so "Back to proforma" never
  // loses work (TDI-394, S4-005). Held in memory only: a refresh still starts
  // the decision screens again until a stored draft is agreed.
  const decisionDraftsRef = useRef({});

  // Available clinicians for assignment dropdown (excludes current user)
  const [availableClinicians, setAvailableClinicians] = useState([]);

  // The signed-in clinician's own rmsProviderUsers profile. Staff management
  // records their free-text role and registration (TD-38); the decision form
  // and the outcome letter pre-fill from it so it never has to be re-typed.
  const [myProviderProfile, setMyProviderProfile] = useState(null);

  // School user profile for the referrer (provides email fallback for older records
  // that may not have referrer.email stored on the patient record)
  const [schoolUserProfile, setSchoolUserProfile] = useState(null);

  // ─── Data Loading ────────────────────────────────────────────────────────

  // Fetch available clinicians for the triage decision dropdown.
  // Reads from the org-scoped rmsProviderUsers subcollection (the dedicated
  // RMS provider collection) instead of the top-level clinicians collection.
  // Excludes the current user from the list.
  useEffect(() => {
    const fetchClinicians = async () => {
      // The auth UID lives on the user object — Firebase token claims carry
      // user_id/sub but NOT uid, so a claims.uid guard never passes and this
      // effect silently never ran (empty clinician list, no role prefill).
      if (!orgId || !user?.uid) return;
      
      try {
        const providerUsersRef = collection(
          db, 'rms_organisations', orgId, 'rmsProviderUsers'
        );
        
        const snapshot = await getDocs(providerUsersRef);
        const mine = snapshot.docs.find((d) => (d.data().accountId || d.id) === user.uid);
        setMyProviderProfile(mine ? mine.data() : null);
        const cliniciansList = snapshot.docs
          .map((doc) => ({
            // Use accountId (Firebase Auth UID) as the clinician identifier
            // to maintain compatibility with triage decision assignments
            clinicianId: doc.data().accountId || doc.id,
            firstName: doc.data().firstName || '',
            lastName: doc.data().lastName || '',
            userType: doc.data().userType || '',
            // Specialism (asd / adhd / both) — the ADHD decision wizard filters
            // its clinicians-involved roster to ADHD-capable clinicians.
            specialism: doc.data().specialism || '',
            // TDI-703: who counts as clinical, and how to tell two people
            // with the same name apart (job title, else email).
            isAssignableClinician: doc.data().isAssignableClinician === true,
            jobTitle: doc.data().role || '',
            email: doc.data().email || '',
          }))
          .filter((c) => c.clinicianId !== user.uid)
          .sort((a, b) => {
            const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
            const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
            return nameA.localeCompare(nameB);
          });
        
        setAvailableClinicians(cliniciansList);
      } catch (err) {
        console.error('[RmsTriageProforma] Failed to fetch clinicians:', err);
      }
    };
    
    fetchClinicians();
  }, [orgId, user?.uid]);

  // Load patient record
  useEffect(() => {
    // orgId is derived from auth claims which load asynchronously;
    // wait for it rather than showing an error during the loading window
    if (!orgId || !patientRecordId) return;

    const patientRef = doc(db, 'rms_organisations', orgId, 'patientRecords', patientRecordId);
    const unsubscribe = onSnapshot(
      patientRef,
      (snap) => {
        if (!snap.exists()) {
          setError('Patient record not found');
          setLoading(false);
          return;
        }
        setPatientRecord({ id: snap.id, ...snap.data() });
        setLoading(false);
      },
      (err) => {
        console.error('[RmsTriageProforma] Error loading patient:', err);
        // TDI-475: a withdrawal clears the clinical access grant and the
        // rules shut this listener. That is the stop message's cue, not a
        // generic failure.
        setListenerError(err);
        if (!String(err?.code || '').includes('permission-denied')) {
          setError('Failed to load patient record');
        }
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [orgId, patientRecordId]);

  // Decided with the other hooks, before any early return (rules of hooks).
  const withdrawnStop = useConsentWithdrawnStop({ patientRecord, listenerError });

  // Fetch the school user profile for the referrer who created this patient record.
  // This provides a fallback for referrer email/name on older records where
  // the referrer object on the patient record may not contain these fields.
  // We look up the school user by their accountId (= createdByUserId on the patient record).
  useEffect(() => {
    if (!orgId || !patientRecord?.createdByUserId) return;
    let cancelled = false;

    const fetchSchoolUser = async () => {
      try {
        const schoolUsersQuery = query(
          collection(db, 'rms_organisations', orgId, 'schoolUsers'),
          where('accountId', '==', patientRecord.createdByUserId)
        );
        const snap = await getDocs(schoolUsersQuery);
        if (!cancelled && !snap.empty) {
          setSchoolUserProfile(snap.docs[0].data() || null);
        }
      } catch (err) {
        console.error('[RmsTriageProforma] Error fetching school user profile:', err);
      }
    };

    fetchSchoolUser();
    return () => { cancelled = true; };
  }, [orgId, patientRecord?.createdByUserId]);

  // Load the form instances a provider may read (TDI-574): the whole
  // subcollection once the family has shared the profile, otherwise the
  // non-HFP instances one by one, since the rule withholds the strengths and
  // needs answers and a collection read would be refused outright.
  const profileSharedFlag = profileSharedForRules(patientRecord);
  const nonHfpIdsKey = patientRecord ? nonHfpInstanceIds(patientRecord).join(',') : '';
  useEffect(() => {
    if (!orgId || !patientRecordId || !patientRecord) return undefined;

    const unsubscribe = subscribeProviderFormInstances(
      { orgId, patientRecordId, record: patientRecord },
      (snapshot) => setFormInstances(instancesById(snapshot)),
      (err) => {
        console.error('[RmsTriageProforma] Error loading form instances:', err);
      }
    );

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, patientRecordId, profileSharedFlag, nonHfpIdsKey]);

  // Load schemas for all form-based sections
  useEffect(() => {
    if (!orgId || Object.keys(formInstances).length === 0) return;

    const loadSchemas = async () => {
      const loadedSchemas = {};
      const getSchema = httpsCallable(functions, 'rmsGetFormSchema');

      // All sections now have form schemas to fetch (the old referralInitiation
      // patient-record-only section has been merged into the Referral Overview)
      const formSections = PROFORMA_SECTIONS;

      for (const section of formSections) {
        try {
          // For dual-respondent forms, try to get schema from either instance
          const guardianInstanceId = `${section.key}_guardian`;
          const schoolInstanceId = `${section.key}_school_user`;
          const instance = formInstances[guardianInstanceId] || formInstances[schoolInstanceId];
          const schemaVersion = instance?.schemaVersion;

          const result = await getSchema({
            orgId,
            formKey: section.key,
            version: schemaVersion,
          });

          if (result.data?.schema) {
            loadedSchemas[section.key] = result.data.schema;
          }
        } catch (err) {
          console.warn(`[RmsTriageProforma] Could not load schema for ${section.key}:`, err.message);
        }
      }

      setSchemas(loadedSchemas);
    };

    loadSchemas();
  }, [orgId, formInstances]);

  // Track when user navigates to a new section - mark it as visited
  useEffect(() => {
    setVisitedSections((prev) => {
      if (prev.has(currentSectionIndex)) return prev;
      const next = new Set(prev);
      next.add(currentSectionIndex);
      return next;
    });
  }, [currentSectionIndex]);

  // ─── Computed Values ─────────────────────────────────────────────────────

  // Compute section statuses
  const sectionStatuses = useMemo(() => {
    const statuses = {};

    PROFORMA_SECTIONS.forEach((section) => {
      if (section.isReferralOverview) {
        // Referral Overview combines patientRecord data with requestForReferral form
        // instances. We consider it complete when we have the patient record AND both
        // form instances are submitted; in_progress if we have partial data.
        const guardianInstance = formInstances[`${section.key}_guardian`];
        const schoolInstance = formInstances[`${section.key}_school_user`];
        const guardianComplete = guardianInstance?.status === 'submitted';
        const schoolComplete = schoolInstance?.status === 'submitted';
        const hasPatientRecord = !!patientRecord;

        if (hasPatientRecord && guardianComplete && schoolComplete) {
          statuses[section.key] = 'complete';
        } else if (hasPatientRecord || guardianInstance || schoolInstance) {
          statuses[section.key] = 'in_progress';
        } else {
          statuses[section.key] = 'not_started';
        }
        return;
      }

      if (section.isDualRespondent) {
        // For dual-respondent forms, check both school and guardian
        const guardianInstance = formInstances[`${section.key}_guardian`];
        const schoolInstance = formInstances[`${section.key}_school_user`];
        const guardianComplete = guardianInstance?.status === 'submitted';
        const schoolComplete = schoolInstance?.status === 'submitted';

        if (guardianComplete && schoolComplete) {
          statuses[section.key] = 'complete';
        } else if (guardianInstance || schoolInstance) {
          statuses[section.key] = 'in_progress';
        } else {
          statuses[section.key] = 'not_started';
        }
      } else {
        // Guardian-only forms
        const instance = formInstances[`${section.key}_guardian`];
        if (!instance) {
          statuses[section.key] = 'not_started';
        } else if (instance.status === 'submitted') {
          statuses[section.key] = 'complete';
        } else {
          statuses[section.key] = 'in_progress';
        }
      }
    });

    return statuses;
  }, [formInstances, patientRecord]);

  // Check if all sections have been visited (user has navigated to each at least once)
  // Used to determine the primary/secondary state of the Complete Review button
  const allSectionsVisited = useMemo(() => {
    return PROFORMA_SECTIONS.every((_, idx) => visitedSections.has(idx));
  }, [visitedSections]);

  // Smooth-scroll the content area to the top whenever the active section changes
  useEffect(() => {
    contentAreaRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentSectionIndex]);

  // Current section data
  const currentSection = PROFORMA_SECTIONS[currentSectionIndex];
  const currentSchema = schemas[currentSection?.key] || {};
  const isLastSection = currentSectionIndex === PROFORMA_SECTIONS.length - 1;

  // Patient name for dialog
  const patientName = useMemo(() => {
    if (!patientRecord) return '';
    const subject = patientRecord.subject || {};
    const firstName = subject.firstName || patientRecord.firstName || '';
    const lastName = subject.lastName || patientRecord.lastName || '';
    return [firstName, lastName].filter(Boolean).join(' ') || 'Unknown';
  }, [patientRecord]);

  /**
   * Extract respondent details from form instances.
   * For school user: looks for school_role (job title) and name from requestForReferral answers
   * For guardian: looks for relationship_cyp and name from requestForReferral answers
   * Falls back to patient record referrer data if no form instance data available.
   */
  const respondentDetails = useMemo(() => {
    // Default values
    const defaults = {
      school: { roleLabel: 'School Representative', name: null },
      guardian: { roleLabel: 'Parent / Carer', name: null },
    };

    // Try to get school user details from requestForReferral form instance
    const schoolInstance = formInstances['requestForReferral_school_user'];
    if (schoolInstance?.answers) {
      const answers = schoolInstance.answers;
      
      // Get school user's role - may be string or object with optionId
      const schoolRole = answers.school_role;
      if (schoolRole) {
        // Map common role optionIds to labels (if it's an object)
        const roleLabels = {
          teacher: 'Teacher',
          senco: 'SENCO',
          head_teacher: 'Head Teacher',
          teaching_assistant: 'Teaching Assistant',
          learning_support: 'Learning Support',
          pastoral_lead: 'Pastoral Lead',
          other: 'School Staff',
        };
        if (typeof schoolRole === 'object' && schoolRole.optionId) {
          defaults.school.roleLabel = roleLabels[schoolRole.optionId] || schoolRole.optionId;
          // If "other" with custom text, use that
          if (schoolRole.optionId === 'other' && schoolRole.otherText) {
            defaults.school.roleLabel = schoolRole.otherText;
          }
        } else if (typeof schoolRole === 'string') {
          defaults.school.roleLabel = roleLabels[schoolRole] || schoolRole;
        }
      }
      
      // Get school user's name from referrer fields (ref_first_name, ref_last_name)
      const firstName = (answers.ref_first_name || '').trim();
      const lastName = (answers.ref_last_name || '').trim();
      if (firstName || lastName) {
        defaults.school.name = [firstName, lastName].filter(Boolean).join(' ');
      }
    }

    // Try to get guardian details from requestForReferral form instance
    const guardianInstance = formInstances['requestForReferral_guardian'];
    if (guardianInstance?.answers) {
      const answers = guardianInstance.answers;
      
      // Get guardian's relationship to CYP
      const relationship = answers.relationship_cyp;
      if (relationship) {
        const relationshipLabels = {
          parent: 'Parent',
          legal_guardian: 'Legal Guardian',
          foster_carer: 'Foster Carer',
          kinship_carer: 'Kinship Carer',
          other: 'Carer',
        };
        if (typeof relationship === 'object' && relationship.optionId) {
          defaults.guardian.roleLabel = relationshipLabels[relationship.optionId] || relationship.optionId;
          if (relationship.optionId === 'other' && relationship.otherText) {
            defaults.guardian.roleLabel = relationship.otherText;
          }
        } else if (typeof relationship === 'string') {
          defaults.guardian.roleLabel = relationshipLabels[relationship] || relationship;
        }
      }
      
      // Get guardian's name (lg_first_name, lg_last_name)
      const firstName = (answers.lg_first_name || '').trim();
      const lastName = (answers.lg_last_name || '').trim();
      if (firstName || lastName) {
        defaults.guardian.name = [firstName, lastName].filter(Boolean).join(' ');
      }
    }

    // Fall back to patient record referrer data if guardian initiated and no form data
    if (!defaults.guardian.name && patientRecord?.referrerType === 'guardian' && patientRecord?.referrer) {
      const referrer = patientRecord.referrer;
      if (referrer.fullName) {
        defaults.guardian.name = referrer.fullName;
      } else if (referrer.firstName || referrer.lastName) {
        defaults.guardian.name = [referrer.firstName, referrer.lastName].filter(Boolean).join(' ');
      }
      if (referrer.relationshipLabel) {
        defaults.guardian.roleLabel = referrer.relationshipLabel;
      }
    }

    // Fall back to patient record referrer data if school initiated and no form data
    if (!defaults.school.name && patientRecord?.referrerType === 'school' && patientRecord?.referrer) {
      const referrer = patientRecord.referrer;
      if (referrer.fullName) {
        defaults.school.name = referrer.fullName;
      } else if (referrer.firstName || referrer.lastName) {
        defaults.school.name = [referrer.firstName, referrer.lastName].filter(Boolean).join(' ');
      }
      if (referrer.relationshipLabel) {
        defaults.school.roleLabel = referrer.relationshipLabel;
      }
    }

    return defaults;
  }, [formInstances, patientRecord]);

  // Determine the existing triage review context (actingPathway is resolved
  // above, before the factor surfacing, so both read the same pathway).
  // An ordered array of clinician review objects. We pass it to the decision
  // form so it can determine whether this will be a 1st or 2nd review and
  // whether the current clinician has already reviewed. For a combined referral
  // these live per-pathway under triagePathways.{pathway}; pathwayReviews falls
  // back to the legacy top-level `triageReviews` for single-concern and
  // pre-split records. For backwards compatibility we also check the legacy
  // `triageDecision` field below.
  const existingTriageReviews = pathwayReviews(patientRecord, actingPathway);
  const existingTriageDecision = existingTriageReviews.length > 0
    ? existingTriageReviews[existingTriageReviews.length - 1]
    : (patientRecord?.triageDecision || null);

  // Attribution for the recorded decision — "Decided by {name}" plus, for
  // structured ADHD MDT decisions, "· with {colleagues}". The decided review is
  // the accepted one when present, otherwise the final review (e.g. the 2nd
  // rejection that discharges an autism referral).
  const decidedReviewIndex = (() => {
    if (existingTriageReviews.length === 0) return -1;
    const acceptedIndex = existingTriageReviews.findIndex((r) => r?.accepted);
    return acceptedIndex >= 0 ? acceptedIndex : existingTriageReviews.length - 1;
  })();
  const decisionAttributionLabel = formatDecisionAttribution(
    getDecisionAttribution(existingTriageReviews),
  );

  // Both the ADHD and autism pathways use the structured decision wizard
  // (AdhdTriageDecisionForm); the `pathway` prop selects the page-1 content and
  // labels. The legacy LLM TriageDecisionDialog remains only as a fallback for
  // an unresolved pathway (a combined referral opened by a both-specialism
  // clinician with no active tab).
  const useStructuredForm =
    actingPathway === PATHWAY.ADHD || actingPathway === PATHWAY.ASD;

  // Roster for the ADHD wizard's clinicians-involved control (TDI-703): only
  // the clinicians who can share an ADHD decision, never Support Users or
  // Non-clinical Org Admins, whatever specialism is stored. The rule lives in
  // adhdDecisionRoster.
  const adhdClinicianRoster = useMemo(
    () => adhdDecisionRoster(availableClinicians, user?.uid),
    [availableClinicians, user?.uid],
  );

  // TD-45: when this is a second review (1st clinician rejected), surface the
  // first reviewer's rationale so the 2nd reviewer can read it before deciding.
  const priorRejection =
    existingTriageReviews.length === 1 && existingTriageReviews[0]?.accepted === false
      ? existingTriageReviews[0]
      : null;

  // View-only: once the triage is terminal (complete) or this clinician has
  // already submitted their decision, the proforma is read-only — the decision
  // cannot be edited or re-submitted. A different clinician can still complete a
  // pending second review. Mirrors the server guard in rmsStartTriage /
  // rmsSubmitTriageDecision.
  const pathwayTriageState = pathwayTriage(patientRecord, actingPathway) || {};
  const triageStatus = pathwayTriageState.status;
  const triageComplete = triageStatus === 'complete';
  // Whether this clinician holds the triage lock on this pathway — they have
  // assigned the referral to themselves and it is theirs to work on. Same test
  // the patient overview uses to decide Start vs Resume triage.
  const triageAssignedToMe = triageStatus === 'in_triage'
    && Boolean(pathwayTriageState.assignedClinicianId)
    && pathwayTriageState.assignedClinicianId === user?.uid;
  const hasMyReview = existingTriageReviews.some(
    (r) => (r?.decidedBy?.clinicianId) === user?.uid,
  );
  const triageViewOnly = triageComplete || hasMyReview;
  // TDI-695: the deciding clinician may change their decision until the
  // letter is signed off; the banners say so. Once they reopen it, their own
  // superseded answers seed the decision screens.
  const decisionChange = triageDecisionChange(patientRecord, actingPathway, user?.uid);
  const reopenedReview = reopenedReviewFor(patientRecord, actingPathway, user?.uid);
  // In preview the proforma is also read-only (no decision possible yet).
  const proformaReadOnly = triageViewOnly || isPreview;
  const viewerIsFirstReviewer = Boolean(priorRejection && priorRejection.decidedBy?.clinicianId === user?.uid);
  // LSC-039/LSC-040: the first reviewer's identity is never shown on triage
  // screens while the second review is pending — the second opinion must be
  // independent. The post-completion reveal (TD-71) names every reviewer.

  // Breadcrumbs for triage proforma - includes patient name when available
  // Must be defined before any early returns to comply with React hooks rules
  const breadcrumbs = useMemo(() => [
    { label: 'Home', path: '/rms/provider/dashboard' },
    // Carry the acting pathway so a combined referral's overview keeps showing
    // THIS pathway's triage state after the breadcrumb hop.
    { label: patientName || 'Patient Overview', path: `/rms/provider/patient/${patientRecordId}${actingPathway ? `?pathway=${actingPathway}` : ''}` },
    { label: 'Triage Proforma', path: null }
  ], [patientRecordId, actingPathway, patientName]);

  // ─── Handlers ────────────────────────────────────────────────────────────

  const handlePrevSection = useCallback(() => {
    setCurrentSectionIndex((prev) => Math.max(0, prev - 1));
  }, []);

  const handleNextSection = useCallback(() => {
    setCurrentSectionIndex((prev) => Math.min(PROFORMA_SECTIONS.length - 1, prev + 1));
  }, []);

  const handleCompleteReview = useCallback(() => {
    // Defensive: never open the decision dialog when read-only (view-only or preview).
    if (proformaReadOnly) return;
    setShowDecisionDialog(true);
  }, [proformaReadOnly]);

  // Preview → Start triage: confirm (assign + lock), then drop preview mode.
  const [showStartConfirm, setShowStartConfirm] = useState(false);
  const handleConfirmStartTriage = useCallback((explicitPathway) => {
    if (!orgId || !patientRecordId) return;
    // Drop preview immediately so the proforma is usable without waiting on the
    // callable (which can cold-start). The lock is acquired in the background;
    // editability isn't gated on it, and the decision callable still enforces
    // the assignee server-side. If the start fails (e.g. another clinician took
    // it), surface the error and return to preview. The pathway is preserved in
    // the URL so a combined referral stays on the pathway being triaged.
    // A both-specialism clinician on a combined referral has no implied
    // pathway (actingPathway is null), so the confirm dialog asks and passes
    // it explicitly (TD-173).
    const pathway =
      explicitPathway === 'adhd' || explicitPathway === 'asd'
        ? explicitPathway
        : actingPathway;
    const pathwayQuery = pathway ? `&pathway=${pathway}` : '';
    setShowStartConfirm(false);
    navigate(
      `/rms/provider/patient/${patientRecordId}/triage${pathway ? `?pathway=${pathway}` : ''}`,
      { replace: true },
    );
    apiStartTriage({ orgId, patientRecordId, pathway }).catch((err) => {
      toast.error(err?.message || 'Could not start triage');
      navigate(
        `/rms/provider/patient/${patientRecordId}/triage?preview=1${pathwayQuery}`,
        { replace: true },
      );
    });
  }, [orgId, patientRecordId, navigate, actingPathway]);

  // Tracks whether the most recent triage decision was a "final" outcome
  // (acceptance or dual-discharge) so we know to redirect on dialog close.
  const [pendingDocumentRedirect, setPendingDocumentRedirect] = useState(false);
  // A first rejection (awaiting a second opinion) is not a final outcome: no
  // letter is generated, so rather than the documents dashboard the clinician is
  // returned to the patient overview, where the recorded decision is flagged
  // (TD-141).
  const [pendingOverviewRedirect, setPendingOverviewRedirect] = useState(false);

  const handleDecisionSuccess = useCallback((context) => {
    console.log('[RmsTriageProforma] Triage decision saved successfully');
    if (context?.isFinalOutcome) {
      setPendingDocumentRedirect(true);
    } else {
      setPendingOverviewRedirect(true);
    }
  }, []);

  const handleDecisionDialogChange = useCallback((open) => {
    setShowDecisionDialog(open);
    if (open) return;
    // On close: a final outcome goes to the documents dashboard to review the
    // generated letter; a first rejection (awaiting a second opinion) returns to
    // the patient overview, where the recorded decision is flagged (TD-141).
    if (pendingDocumentRedirect) {
      setPendingDocumentRedirect(false);
      navigate(
        `/rms/provider/patient/${patientRecordId}/documents${actingPathway ? `?pathway=${actingPathway}` : ''}`,
      );
    } else if (pendingOverviewRedirect) {
      setPendingOverviewRedirect(false);
      navigate(
        `/rms/provider/patient/${patientRecordId}${actingPathway ? `?pathway=${actingPathway}` : ''}`,
      );
    }
  }, [pendingDocumentRedirect, pendingOverviewRedirect, navigate, patientRecordId, actingPathway]);

  // ── Structured decision (four-step wizard) — ADHD and autism ───────────────
  // The structured form is self-contained, so the proforma owns the submit + the
  // letter-generation trigger for both pathways.
  //   - ADHD: a single clinician's decision is final either way, so the outcome
  //     letter is generated for every completed decision.
  //   - Autism: a dual-review flow. Acceptance and the SECOND review (the second
  //     rejection that discharges) are final outcomes that generate the letter;
  //     a FIRST rejection is forwarded for a second opinion — no letter yet, and
  //     the clinician is returned to the patient overview (TD-141) rather than
  //     the documents dashboard.
  const [adhdSubmitting, setAdhdSubmitting] = useState(false);
  const handleStructuredConfirm = useCallback(async (confirmation) => {
    if (!orgId || !patientRecordId || adhdSubmitting) return;
    // Never guess the pathway: a silent ADHD default recorded a combined
    // referral's autism decision against the ADHD pathway when the ?pathway=
    // context had been dropped. Fail loudly instead — the decision is the
    // record of truth and must land on the pathway the clinician triaged.
    const pathwayUsed = confirmation.pathway || actingPathway;
    if (!pathwayUsed) {
      toast.error(
        'Could not determine which pathway this decision is for. Please reopen the referral from the dashboard pathway tab and try again.',
      );
      return;
    }
    setAdhdSubmitting(true);
    // Save the decision first. This is the record of truth; the letter is a
    // downstream artefact. If the save itself fails, keep the wizard open so
    // the clinician can retry.
    let reviewIndex = 0;
    let letterQueued = false;
    try {
      const submit = httpsCallable(functions, 'rmsSubmitTriageDecision');
      // The structured decision contract. Decisions carry the wizard record and
      // do NOT write triageOutcomeCode / letterVariables / priorityLevel /
      // pathwayAllocation (historical records keep theirs).
      const payload = {
        orgId,
        patientRecordId,
        pathway: pathwayUsed,
        accepted: confirmation.accepted,
        evidence: confirmation.evidence,
        otherFactors: confirmation.otherFactors,
        conclusion: confirmation.conclusion,
        signposting: confirmation.signposting,
        // Signposting pick-list (RMS-309): the selected services and the
        // clinician's "Other", which the letter assembles deterministically.
        signpostingSelections: confirmation.signpostingSelections,
        signpostingOther: confirmation.signpostingOther,
        // Always false since the manual consensus tick was removed for
        // go-live (TDI-784, R-053); an outcome disagreement still auto-flags
        // server-side, recorded in the audit trail and shown nowhere.
        consensusFlagRequested: confirmation.consensusFlagRequested || false,
        // Autism second review only: per question, whether the second
        // reviewer kept or changed the first review's answer (TDI-401).
        firstReviewComparison: confirmation.firstReviewComparison,
        snapSnapshot: confirmation.snapSnapshot,
        surfacedFactors: confirmation.surfacedFactors,
        // Autism only: clinician-applied DSM-5 criterion tags on referral
        // answers (undefined for ADHD — dropped in serialisation).
        evidenceTags: confirmation.evidenceTags,
        cliniciansInvolved: confirmation.cliniciansInvolved,
      };
      const result = await submit(payload);
      if (!result?.data?.success) {
        throw new Error(result?.data?.message || 'Failed to save triage decision');
      }
      reviewIndex = result?.data?.reviewIndex ?? 0;
      letterQueued = result?.data?.letterQueued === true;
    } catch (err) {
      toast.error(err?.message || 'Could not save triage decision');
      setAdhdSubmitting(false);
      return;
    }

    // The wizard is done regardless of what happens next — close it so a letter
    // failure can never strand the clinician on a submitted decision. The
    // decision is recorded, so its unsent draft goes with it.
    delete decisionDraftsRef.current[`${patientRecordId}:${pathwayUsed}`];
    setShowDecisionDialog(false);

    // Finality mirrors the backend transition: ADHD is always terminal; autism
    // is terminal on acceptance or on the second review (reviewIndex >= 1). An
    // autism first rejection is not final — no letter is generated.
    const isFinalOutcome =
      pathwayUsed === PATHWAY.ADHD || confirmation.accepted || reviewIndex >= 1;

    if (!isFinalOutcome) {
      setAdhdSubmitting(false);
      navigate(`/rms/provider/patient/${patientRecordId}?pathway=${pathwayUsed}`);
      return;
    }

    // Terminal outcome: navigate to the documents view immediately, then kick
    // off paragraph generation in the background — the generation call is a
    // multi-second LLM run and awaiting it here made the clinician stare at the
    // submitted wizard for 3-5s. The documents dashboard listens to
    // triageDocuments.processingStatus via onSnapshot (the callable sets
    // "processing" up front and "complete" at the end), so it shows a
    // "generating" state until the paragraphs arrive. We deliberately do NOT
    // render the PDF here — the paragraphs are AI-generated and unapproved at
    // this point, so a render would fail with "No approved paragraphs found".
    // Rendering is triggered from the documents review once the clinician has
    // approved all sections (RmsDocumentReview.handleExitReview). Generation
    // failures are surfaced as a non-blocking notice; the decision stands and
    // generation can be retried from the Documents view.
    setAdhdSubmitting(false);
    navigate(
      `/rms/provider/patient/${patientRecordId}/documents?view=1&pathway=${pathwayUsed}`,
    );
    // TDI-396: the server queues the drafts itself the moment the decision
    // is final (letterQueued), so a closed tab or a slow machine no longer
    // costs the letter. The browser's own call stands in only when the queue
    // could not take the task.
    if (letterQueued) return;
    const generate = httpsCallable(functions, 'rmsGenerateTriageLetter', { timeout: 300000 });
    generate({ orgId, patientRecordId, pathway: pathwayUsed }).catch(() => {
      toast.error(
        'Decision recorded. The outcome letter could not be generated yet — it can be generated from the documents view.',
      );
    });
  }, [orgId, patientRecordId, adhdSubmitting, navigate, actingPathway]);

  const handleViewLineItem = useCallback(
    (question, answer, respondentLabel, questionsGroup = null, allAnswers = null, respondentRole = null) => {
      // Resolve the form instance for this respondent so the dialog can show
      // the submission timestamp. For patient record data (no role), fall back
      // to the patientRecord itself which carries its own timestamps.
      let instance = null;
      if (respondentRole && currentSection) {
        instance = formInstances[`${currentSection.key}_${respondentRole}`] || null;
      }
      if (!instance && patientRecord) {
        instance = { submittedAt: patientRecord.createdAt || patientRecord.updatedAt || null };
      }
      setLineItemDialog({
        isOpen: true,
        question,
        answer,
        questionsGroup,
        allAnswers,
        formName: currentSection?.title,
        formInstance: instance,
        respondentLabel,
      });
    },
    [currentSection, formInstances, patientRecord]
  );

  const handleCloseLineItem = useCallback(() => {
    setLineItemDialog((prev) => ({ ...prev, isOpen: false }));
  }, []);

  // ─── Evidence tagging ─────────────────────────────────────────────────────
  // A clinician reads the proforma before they open the decision wizard, so
  // this is where they notice that an answer is the evidence for a criterion.
  // Tags made here are stored on the referral and the wizard opens with them
  // already applied, instead of the tagging affordance living only inside the
  // wizard and being lost when it closes.
  //
  // Only the caller's own tags are read: the server stores them per clinician
  // so an autism second opinion starts from that reviewer's own reading.
  const myEvidenceTags = useMemo(
    () => pathwayEvidenceTags(patientRecord, actingPathway, user?.uid),
    [patientRecord, actingPathway, user?.uid],
  );

  const handleAddEvidenceTag = useCallback(async (tag) => {
    try {
      await apiUpdateEvidenceTags({
        orgId,
        patientRecordId,
        pathway: actingPathway,
        action: 'add',
        tag,
      });
      // The patient record is live, so the new tag arrives on the snapshot.
      return true;
    } catch (e) {
      console.error('[RmsTriageProforma] add evidence tag failed', e);
      toast.error(e?.message || 'The tag could not be saved. Please try again.');
      // The tag form keeps the clinician's choices and note to try again.
      return false;
    }
  }, [orgId, patientRecordId, actingPathway]);

  const handleRemoveEvidenceTag = useCallback(async (tagId) => {
    try {
      await apiUpdateEvidenceTags({
        orgId,
        patientRecordId,
        pathway: actingPathway,
        action: 'remove',
        tagId,
      });
    } catch (e) {
      console.error('[RmsTriageProforma] remove evidence tag failed', e);
      toast.error(e?.message || 'The tag could not be removed. Please try again.');
    }
  }, [orgId, patientRecordId, actingPathway]);

  // Handed to every panel so each category heading can carry the Tag button,
  // or null for a user who cannot triage at all - a permanently dead button
  // is worse than no button.
  //
  // Tagging opens only once the clinician has assigned the referral to
  // themselves (Eve, 29 Sept). Tagging is part of triaging, not of reading:
  // in preview the referral is not yet anyone's, and a clinician who looks at
  // the forms and moves on should leave no annotations on a record another
  // clinician will pick up. It closes again once they have decided. The
  // server enforces the same two conditions.
  const tagging = useMemo(() => (!isClinicianRole ? null : {
    pathway: actingPathway,
    disabled: !triageAssignedToMe || triageViewOnly,
    disabledReason: triageViewOnly
      ? 'This referral has been decided, so its tags can no longer be changed'
      : 'Start triage on this referral to tag evidence',
    tagsFor: (formKey, role, questionId) => myEvidenceTags.filter(
      (t) => t.formKey === formKey && t.role === role && t.questionId === questionId,
    ),
    onAdd: handleAddEvidenceTag,
    onRemove: handleRemoveEvidenceTag,
  }), [actingPathway, triageAssignedToMe, triageViewOnly, isClinicianRole,
       myEvidenceTags, handleAddEvidenceTag, handleRemoveEvidenceTag]);

  // ─── Render Helpers ──────────────────────────────────────────────────────

  /**
   * Renders the content for the current section based on its type
   */
  const renderSectionContent = () => {
    if (!currentSection) return null;

    // Get the schema for this section
    const schema = currentSchema;

    // Referral Overview — combined Referral Initiation + Request for Referral.
    // Merges patientRecord data (school's referral initiation) with the
    // requestForReferral form data (school + guardian instances) into one view.
    if (currentSection.isReferralOverview) {
      if (!schema?.sections || schema.sections.length === 0) {
        return (
          <div className="bg-gray-50 rounded-lg border border-gray-200 p-8 text-center">
            <p className="text-gray-500">
              {sectionStatuses[currentSection.key] === 'not_started'
                ? 'No data available for this form.'
                : 'Loading form structure...'}
            </p>
          </div>
        );
      }

      const schoolInstance = formInstances[`${currentSection.key}_school_user`] || {};
      const guardianInstance = formInstances[`${currentSection.key}_guardian`] || {};

      return (
        <ReferralOverviewPanel
          patientRecord={patientRecord}
          schoolUserProfile={schoolUserProfile}
          schema={schema}
          formKey={currentSection.key}
          schoolAnswers={schoolInstance.answers || {}}
          guardianAnswers={guardianInstance.answers || {}}
          hasSchoolInstance={!!formInstances[`${currentSection.key}_school_user`]}
          hasGuardianInstance={!!formInstances[`${currentSection.key}_guardian`]}
          onViewLineItem={handleViewLineItem}
          tagging={tagging}
          riskAlerts={urgentRiskAlerts}
        />
      );
    }

    if (!schema?.sections || schema.sections.length === 0) {
      return (
        <div className="bg-gray-50 rounded-lg border border-gray-200 p-8 text-center">
          <p className="text-gray-500">
            {sectionStatuses[currentSection.key] === 'not_started'
              ? 'No data available for this form.'
              : 'Loading form structure...'}
          </p>
        </div>
      );
    }

    // Dual-respondent form - show two columns with both school and guardian responses.
    //
    // The form schemas gate sections by respondent role (school_user vs guardian) using
    // visibleWhen conditions. Many questions are asked of BOTH respondents but live in
    // different sections with different questionIds — they're linked by sharing the same
    // categoryOfInformation value (e.g. "Main concerns" → school `main_concerns` +
    // guardian `lg_biggest_worries`).
    //
    // buildMergedSections() pre-processes the schema to merge these matching questions
    // into unified rows, so the proforma shows them side-by-side. Questions unique to
    // one respondent show "N/A" for the other column.
    if (currentSection.isDualRespondent) {
      const schoolInstance = formInstances[`${currentSection.key}_school_user`] || {};
      const guardianInstance = formInstances[`${currentSection.key}_guardian`] || {};
      const schoolAnswers = schoolInstance.answers || {};
      const guardianAnswers = guardianInstance.answers || {};

      // Determine the referral initiation type (school vs guardian) so we can correctly
      // evaluate visibleWhen conditions that depend on `initiatedBy`
      const initiatedBy = patientRecord?.referrerType || 'school';

      // Check whether each respondent even has a form instance assigned to them.
      // If no form instance exists at all, every question is N/A for that respondent.
      const hasSchoolInstance = !!formInstances[`${currentSection.key}_school_user`];
      const hasGuardianInstance = !!formInstances[`${currentSection.key}_guardian`];

      const mergedPanels = buildMergedSections(schema, initiatedBy);

      return mergedPanels.map((panel) => (
        <DualRespondentDomainPanel
          key={panel.sectionId}
          panel={panel}
          formKey={currentSection.key}
          schoolAnswers={schoolAnswers}
          guardianAnswers={guardianAnswers}
          schoolInfo={respondentDetails.school}
          guardianInfo={respondentDetails.guardian}
          hasSchoolInstance={hasSchoolInstance}
          hasGuardianInstance={hasGuardianInstance}
          onViewLineItem={handleViewLineItem}
          tagging={tagging}
          riskAlerts={urgentRiskAlerts}
        />
      ));
    }

    // Guardian-only form (triage forms) — single column with guardian responses,
    // grouped by Category of Information (CoI).
    const guardianInstance = formInstances[`${currentSection.key}_guardian`] || {};
    const answers = guardianInstance.answers || {};

    return schema.sections.map((section) => (
      <SingleRespondentDomainPanel
        key={section.sectionId}
        section={section}
        answers={answers}
        respondentInfo={respondentDetails.guardian}
        onViewLineItem={handleViewLineItem}
        formKey={currentSection.key}
        tagging={tagging}
        formContext={guardianInstance.context || null}
        riskAlerts={urgentRiskAlerts}
      />
    ));
  };

  // ─── Render ──────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <RefreshIcon className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-3" />
          <p className="text-gray-600">Loading triage proforma...</p>
        </div>
      </div>
    );
  }

  // TDI-475: the family withdrew consent while this screen was open (or the
  // rules shut the record under it). Everything stops; the only way on is
  // back to the dashboard, where the referral no longer appears.
  if (withdrawnStop.stopped) {
    return (
      <div className="min-h-screen bg-white p-8">
        <ConsentWithdrawnDialog open onLeave={() => navigate('/rms/provider/dashboard')} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white p-8">
        <div className="max-w-4xl mx-auto bg-red-50 rounded-xl border border-red-200 p-6 text-center">
          <p className="text-red-700">{error}</p>
          <button
            onClick={() => navigate('/rms/provider/dashboard')}
            className="mt-4 text-sm text-red-600 hover:underline"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // TD-234 (LSC-043): the structured decision wizard is a full-page view, not
  // a popup over the proforma. Reviewing the referral evidence and tagging it
  // against DSM-5 criteria happen inside this workflow (the slide-over evidence
  // panel), so the duplicated proforma-plus-popup split is gone. The legacy
  // TriageDecisionDialog fallback (unresolved pathway) keeps its dialog form.
  if (useStructuredForm && showDecisionDialog) {
    const decisionDraftKey = `${patientRecordId}:${actingPathway}`;
    return (
      <div className="h-screen bg-gray-50 flex flex-col">
        <RmsTopNavBar breadcrumbs={breadcrumbs} />
        {/* The child's identity, the same on every triage screen (TDI-392);
            it replaces the name heading this view used to carry. */}
        <ChildIdentityHeader patient={patientRecord} patientRecordId={patientRecordId} loading={!patientRecord} />
        <div className="flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-4xl px-6 py-8">
            <div className="mb-6 flex items-start justify-between gap-3">
              <div className="text-left">
                <h1
                  className="text-xs font-semibold uppercase tracking-wide"
                  style={{ color: BRAND_PRIMARY }}
                >
                  Triage decision
                </h1>
              </div>
              <button
                type="button"
                onClick={() => setShowDecisionDialog(false)}
                className="whitespace-nowrap rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Back to proforma
              </button>
            </div>

            {/* Autism second review: the first reviewer did not accept. Their
                answers and notes are shown on each step, identity withheld,
                and start as the second reviewer's answers to keep or change
                (TDI-401; Tom King, 23 Sept 2026). */}
            {actingPathway === PATHWAY.ASD && priorRejection && !viewerIsFirstReviewer && (
              <div className="mb-6 rounded-md border border-amber-200 bg-amber-50 p-3">
                <p className="text-xs text-amber-800">
                  This is a second review. Another clinician did not accept this referral
                  at first review. Their answers and notes are shown on each step and start
                  as your answers: keep each one or change it, and add your own notes. Your
                  decision is the final one.
                </p>
              </div>
            )}

            <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
              {actingPathway === PATHWAY.ASD ? (
                <AutismTriageDecisionForm
                  patientName={patientName}
                  patientSubject={patientRecord?.subject}
                  schemas={schemas}
                  formInstances={formInstances}
                  surfacedFactors={surfacedFactors}
                  riskStratification={patientRecord?.riskStratification}
                  showRiskStratification={riskStratificationVisible}
                  isSecondReview={Boolean(priorRejection)}
                  // The clinician's own tags, made here or on the proforma —
                  // the wizard opens with what they already tagged.
                  evidenceTags={myEvidenceTags}
                  onAddTag={handleAddEvidenceTag}
                  onRemoveTag={handleRemoveEvidenceTag}
                  firstReviewEvidence={priorRejection?.evidence || null}
                  // The first reviewer's written work: the structured
                  // wizard records a conclusion, the legacy reject path a
                  // rationale. Shown to the second reviewer per Tom King,
                  // 23 Sept; identity stays masked.
                  firstReviewNotes={priorRejection?.conclusion || priorRejection?.rationale || null}
                  // The first review's factor notes, group comments, risks and
                  // priority problems, shown on the matching steps (TDI-401).
                  firstReviewOtherFactors={priorRejection?.otherFactors || null}
                  // The first reviewer's risk and other-factor tags with their
                  // notes, snapshotted on their review (TDI-394, R-008).
                  firstReviewEvidenceTags={priorRejection?.evidenceTags || null}
                  currentClinician={{
                    clinicianId: user?.uid,
                    // Prefer the structured staff-profile name; displayName can
                    // carry extra text on some accounts.
                    clinicianName:
                      [myProviderProfile?.firstName, myProviderProfile?.lastName]
                        .filter(Boolean)
                        .join(' ')
                      || user?.displayName
                      || claims?.name
                      || '',
                  }}
                  // Answers so far survive "Back to proforma" (TDI-394).
                  draft={decisionDraftsRef.current[decisionDraftKey]}
                  onDraftChange={(next) => { decisionDraftsRef.current[decisionDraftKey] = next; }}
                  // A reopened decision opens with the clinician's own
                  // answers (TDI-695).
                  initialAnswers={reopenedReview
                    ? initialAnswersFromReview(reopenedReview, PATHWAY.ASD)
                    : null}
                  // The check dialog names the pathway on a combined referral
                  // (TDI-693).
                  combinedReferral={isCombinedConcern(patientRecord?.intakeConcern)}
                  busy={adhdSubmitting}
                  onConfirm={handleStructuredConfirm}
                  onCancel={() => setShowDecisionDialog(false)}
                />
              ) : (
                <AdhdTriageDecisionForm
                  patientName={patientName}
                  patientSubject={patientRecord?.subject}
                  schemas={schemas}
                  formInstances={formInstances}
                  // The clinician's own tags, made here or on the proforma —
                  // the wizard opens with what they already tagged.
                  evidenceTags={myEvidenceTags}
                  onAddTag={handleAddEvidenceTag}
                  onRemoveTag={handleRemoveEvidenceTag}
                  surfacedFactors={surfacedFactors}
                  riskStratification={patientRecord?.riskStratification}
                  showRiskStratification={riskStratificationVisible}
                  availableClinicians={adhdClinicianRoster}
                  currentClinician={{
                    clinicianId: user?.uid,
                    // Prefer the structured staff-profile name; displayName can
                    // carry extra text on some accounts.
                    clinicianName:
                      [myProviderProfile?.firstName, myProviderProfile?.lastName]
                        .filter(Boolean)
                        .join(' ')
                      || user?.displayName
                      || claims?.name
                      || '',
                  }}
                  // Answers so far survive "Back to proforma" (TDI-394).
                  draft={decisionDraftsRef.current[decisionDraftKey]}
                  onDraftChange={(next) => { decisionDraftsRef.current[decisionDraftKey] = next; }}
                  // A reopened decision opens with the clinician's own
                  // answers (TDI-695).
                  initialAnswers={reopenedReview
                    ? initialAnswersFromReview(reopenedReview, PATHWAY.ADHD, adhdClinicianRoster)
                    : null}
                  // The check dialog names the pathway on a combined referral
                  // (TDI-693).
                  combinedReferral={isCombinedConcern(patientRecord?.intakeConcern)}
                  busy={adhdSubmitting}
                  onConfirm={handleStructuredConfirm}
                  onCancel={() => setShowDecisionDialog(false)}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-white flex flex-col">
      <RmsTopNavBar breadcrumbs={breadcrumbs} />
      {/* The child's identity stays in view on every section (TDI-392). */}
      <ChildIdentityHeader patient={patientRecord} patientRecordId={patientRecordId} loading={!patientRecord} />

      <div className="flex flex-1 overflow-hidden">
        {/* Side navigation — fixed in place; only the content area scrolls */}
        <ProformaSideNav
          sections={PROFORMA_SECTIONS}
          currentIndex={currentSectionIndex}
          onSelect={setCurrentSectionIndex}
          visitedSections={visitedSections}
          isClinicianRole={isClinicianRole}
          onCompleteReview={handleCompleteReview}
          isSecondReview={existingTriageReviews.length === 1}
          allSectionsVisited={allSectionsVisited}
          viewOnly={triageViewOnly}
          isPreview={isPreview}
          onStartTriage={() => setShowStartConfirm(true)}
          viewerIsFirstReviewer={viewerIsFirstReviewer}
        />

        {/* Content area — sole scrollable region */}
        <div ref={contentAreaRef} className="flex-grow overflow-y-auto p-8">
          {/* Triage state banner. One concise banner:
              - complete            → "Triage complete" (view only)
              - 1st reviewer viewing → combined "Decision submitted — Awaiting
                second opinion" + their own rationale
              - 2nd reviewer        → context only, rationale withheld so their
                independent opinion isn't biased (revealed at discharge-letter time). */}
          {isPreview ? (
            <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4 text-left">
              <p className="text-sm font-semibold text-blue-900">Preview — triage not started</p>
              <p className="text-xs text-blue-800 mt-1">
                You are previewing the school and parent responses. No triage decision can be made
                yet — click <strong>Start triage</strong> to assign and lock this patient to you,
                then make your decision.
              </p>
            </div>
          ) : triageComplete ? (
            <div className="mb-6 bg-gray-50 border border-gray-200 rounded-lg p-4 text-left">
              <p className="text-sm font-semibold text-gray-800">Triage complete</p>
              <p className="text-xs text-gray-600 mt-0.5">
                {decisionChange.allowed
                  ? DECISION_CHANGEABLE_NOTICE
                  : 'This triage decision has been recorded and can no longer be changed.'} You can review the responses below.
              </p>
              {/* Once triage is complete, reveal every reviewer's decision + rationale.
                  The first reviewer's rationale was withheld from the second reviewer
                  while they formed an independent opinion (TD-45); it resurfaces here
                  now that the outcome is final (TD-71). */}
              {existingTriageReviews.length > 0 && (
                <div className="mt-3 space-y-2">
                  {existingTriageReviews.map((rev, i) => {
                    const isMulti = existingTriageReviews.length > 1;
                    const label = isMulti ? (i === 0 ? 'First review' : 'Second review') : 'Decision';
                    const name = rev?.decidedBy?.clinicianName
                      || (i === 0 ? 'First reviewer' : 'Second reviewer');
                    const decision = rev?.accepted ? 'Accepted' : 'Not accepted';
                    const rationale = (rev?.rationale || '').trim();
                    return (
                      <div key={i} className="rounded-md bg-white border border-gray-200 p-3">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                          {label} — {name}
                        </p>
                        <p className="text-sm font-medium text-gray-900 mt-1">{decision}</p>
                        {/* Decision attribution — under the decision that ended
                            triage: who recorded it and, for ADHD MDT decisions,
                            which colleagues were involved. */}
                        {i === decidedReviewIndex && decisionAttributionLabel && (
                          <p className="text-xs text-gray-600 mt-0.5">{decisionAttributionLabel}</p>
                        )}
                        {rationale && (
                          <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">{rationale}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : priorRejection ? (
            viewerIsFirstReviewer ? (
              <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4 text-left">
                <p className="text-sm font-semibold text-amber-900">
                  Decision submitted — Awaiting second opinion
                </p>
                <p className="text-xs text-amber-800 mt-1">
                  {decisionChange.allowed
                    ? FIRST_REJECTION_CHANGEABLE_NOTICE
                    : 'This triage decision has been recorded and can no longer be changed.'} You previously
                  reviewed this referral and decided not to accept. If the second clinician reviewing
                  this patient&rsquo;s referral also rejects, the referral will not be accepted onto
                  the pathway at this stage.
                </p>
                {(priorRejection.rationale || '').trim() && (
                  <div className="mt-2 rounded-md bg-white/70 border border-amber-200 p-3">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-700">
                      Your rationale
                    </p>
                    <p className="text-sm text-amber-900 mt-1 whitespace-pre-wrap">
                      {priorRejection.rationale.trim()}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4 text-left">
                <p className="text-sm font-semibold text-amber-900">Second review</p>
                <p className="text-xs text-amber-800 mt-1">
                  <strong>Another clinician</strong> reviewed this referral and decided not to accept.
                  If you also reject, the referral will not be accepted onto the pathway at
                  this stage.
                </p>
              </div>
            )
          ) : null}

          {/* Navigation bar — Back button, centred stage title, Next button */}
          <div className="flex items-center mb-6">
            <div className="w-28 flex-shrink-0">
              {currentSectionIndex > 0 && (
                <button
                  onClick={handlePrevSection}
                  className="flex items-center py-2 px-4 rounded-md shadow-md border transition-all duration-300 text-gray-900 border-gray-300 hover:bg-gray-50"
                >
                  <ChevronLeftIcon className="w-5 h-5 mr-2" />
                  <span className="font-bold">Back</span>
                </button>
              )}
            </div>

            <div className="flex-1 min-w-0 px-4 text-center">
              <h2 className="text-2xl font-bold text-gray-900 truncate">
                {currentSection?.title}
              </h2>
              {/* The child's identity sits in the shared header above
                  (TDI-392), so it is not repeated under the section title. */}
            </div>

            <div className="w-28 flex-shrink-0 flex justify-end">
              {!isLastSection && (
                <button
                  onClick={handleNextSection}
                  className="flex items-center py-2 px-4 rounded-md shadow-md transition duration-100 text-white hover:opacity-90"
                  style={{ backgroundColor: BRAND_PRIMARY }}
                >
                  <span className="font-bold">Next</span>
                  <ChevronRightIcon className="w-5 h-5 ml-2" />
                </button>
              )}
            </div>
          </div>

          {/* Form status indicator */}
          {sectionStatuses[currentSection?.key] === 'not_started' && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 text-left">
              <p className="text-sm text-amber-700">
                {currentSection?.isDualRespondent
                  ? 'This form has not been completed by either respondent yet.'
                  : 'This form has not been started by the guardian yet.'}
              </p>
            </div>
          )}

          {/* Section content */}
          {renderSectionContent()}

          {/* Existing triage decision banner */}
          {/* {existingTriageDecision && (
            <ExistingDecisionBanner
              decision={existingTriageDecision}
              onEdit={isClinicianRole && !proformaReadOnly ? handleCompleteReview : null}
            />
          )} */}

          {/* Bottom navigation button - hidden on last page (Complete Review is in sidebar) */}
          {!isLastSection && (
            <div className="flex justify-end items-center mt-8 pt-4 border-t border-gray-200">
              <button
                onClick={handleNextSection}
                className="flex items-center py-2 px-4 rounded-md shadow-md transition duration-100 text-white hover:opacity-90"
                style={{ backgroundColor: BRAND_PRIMARY }}
              >
                <span className="font-bold">Next</span>
                <ChevronRightIcon className="w-5 h-5 ml-2" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Line item detail dialog — supports single-question and multi-question (CoI group) modes */}
      <LineItemDialog
        isOpen={lineItemDialog.isOpen}
        onClose={handleCloseLineItem}
        question={lineItemDialog.question}
        answer={lineItemDialog.answer}
        questionsGroup={lineItemDialog.questionsGroup}
        allAnswers={lineItemDialog.allAnswers}
        formName={lineItemDialog.formName}
        formInstance={lineItemDialog.formInstance}
        respondentLabel={lineItemDialog.respondentLabel}
      />

      {/* Triage decision — each pathway's structured wizard now renders as a
          full-page view (TD-234, see the conditional return above this one).
          The legacy TriageDecisionDialog remains only as a fallback for an
          unresolved pathway (a combined referral opened by a both-specialism
          clinician with no active tab). */}
      {!useStructuredForm && (
        <TriageDecisionDialog
          isOpen={showDecisionDialog}
          onOpenChange={handleDecisionDialogChange}
          orgId={orgId}
          patientRecordId={patientRecordId}
          patientName={patientName}
          pathway={actingPathway}
          existingDecision={existingTriageDecision}
          triageReviews={existingTriageReviews}
          riskStratification={
            riskStratificationVisible ? patientRecord?.riskStratification : null
          }
          availableClinicians={availableClinicians}
          onSuccess={handleDecisionSuccess}
        />
      )}

      {/* Start-triage confirmation from preview — makes the assignment + lock
          explicit before the clinician leaves preview and commits. */}
      {showStartConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" role="dialog" aria-modal="true" aria-labelledby="proforma-start-triage-title">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 w-full max-w-md p-6 text-left">
            <h2 id="proforma-start-triage-title" className="text-base font-semibold text-gray-900">
              Start triage{patientName ? ` for ${patientName}` : ''}?
            </h2>
            <p className="text-sm text-gray-600 mt-2">
              This assigns the patient to you and locks the referral to other clinicians until you
              release it or complete a decision.
            </p>
            {/* TD-173: a combined referral is triaged per pathway; with no
                pathway implied (both-specialism clinician, no ?pathway=),
                ask which pathway is being started. */}
            {isCombinedConcern(patientRecord?.intakeConcern) && !actingPathway ? (
              <>
                <p className="text-sm text-gray-600 mt-2">
                  This is a combined referral — each pathway is triaged separately. Which
                  pathway are you starting?
                </p>
                <div className="mt-5 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowStartConfirm(false)}
                    className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  {pathwaysForConcern(patientRecord?.intakeConcern).map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => handleConfirmStartTriage(p)}
                      className="px-5 py-2 text-sm font-medium rounded-lg bg-primary text-white hover:bg-primary/90 shadow-sm"
                    >
                      Start {PATHWAY_LABELS[p] || p} triage
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <div className="mt-5 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowStartConfirm(false)}
                  className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => handleConfirmStartTriage()}
                  className="px-5 py-2 text-sm font-medium rounded-lg bg-primary text-white hover:bg-primary/90 shadow-sm"
                >
                  Start triage
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
